// Absolute jsDelivr "+esm" URLs instead of an import map: the addon bundles
// import three from exactly this URL, so everything shares one Three.js
// instance and nothing depends on where (or whether) an import map is placed.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.167.1/+esm';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.167.1/examples/jsm/controls/OrbitControls.js/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.167.1/examples/jsm/loaders/GLTFLoader.js/+esm';
import { DRACOLoader } from 'https://cdn.jsdelivr.net/npm/three@0.167.1/examples/jsm/loaders/DRACOLoader.js/+esm';
import { GLTFExporter } from 'https://cdn.jsdelivr.net/npm/three@0.167.1/examples/jsm/exporters/GLTFExporter.js/+esm';

// The position guide is a single "board" in logical units. Every garment piece
// is projected into its own panel on the board, and every layer (image, text,
// stroke) lives in board coordinates. A layer therefore prints onto whichever
// pieces it overlaps, and stacking order is simply the order of `this.layers`.
// Keep BOARD_W / BOARD_H in sync with the aspect-ratio in 3d-configuration.css.
const BOARD_W = 652;
const BOARD_H = 756;
// Board texture pixels per board unit. Chest placement (no guide) has no dragging, so it can
// afford a sharper board: on a model whose front panel holds the whole garment (hood and
// sleeves too), the chest print would otherwise get only a few hundred pixels.
const BOARD_SCALE = 2;
const CHEST_BOARD_SCALE = 4;
// Each collar half gets a full-width row: the collar is long and thin, so this
// doubles its size in the guide without distorting its proportions.
const BOARD_PANELS = {
  collarFront: { x: 0, y: 0, width: 652, height: 90, label: 'COLLAR FRONT' },
  collarBack: { x: 0, y: 102, width: 652, height: 90, label: 'COLLAR BACK' },
  front: { x: 0, y: 204, width: 320, height: 340, label: 'FRONT' },
  back: { x: 332, y: 204, width: 320, height: 340, label: 'BACK' },
  left: { x: 0, y: 556, width: 320, height: 200, label: 'LEFT' },
  right: { x: 332, y: 556, width: 320, height: 200, label: 'RIGHT' },
};
const GUIDE_LABEL_COLOR = '#6b6b73';
// Panels in a group share one projection scale so paired pieces stay the same size.
// The collar is unwrapped separately (see unwrapCollar) across both collar panels.
const PANEL_GROUPS = [['front', 'back'], ['left', 'right']];
// A garment modelled as sewn pieces (front, back, sleeves, collar) has a handful of
// connected components. More than this means one shell cut into UV patches (scanned or
// AI-generated models such as the hoodie): its triangles are sorted by the way they face.
const MAX_GARMENT_PIECES = 24;
const PANEL_PADDING = { top: 30, side: 12, bottom: 12 };
const CORNER_SIGNS = [
  { x: -1, y: -1 },
  { x: 1, y: -1 },
  { x: 1, y: 1 },
  { x: -1, y: 1 },
];
// The uploaded design's longest side: the file sent for printing with Buy now (4096 px ≈ 300 DPI
// over a 35 cm print)
const MAX_LAYER_IMAGE = 4096;
// Buy now's design file: the backend and Shopify accept images up to 20 MB
const MAX_DESIGN_BYTES = 20 * 1024 * 1024;
const TEXT_RASTER_SIZE = 160;
const TEXT_LINE_HEIGHT = 1.25;
const LAYOUT_KEY = 'vt-layout-v2';
// uKnit value meaning "knit animation off" (anything >= this skips the reveal).
const KNIT_OFF = 2;
const KNIT_CYCLE = 5; // animation-time units per knit loop
const PUFF_STRENGTH = 5;
// Acid wash field sampling and thresholds, shared by the shader and the GLB bake.
const ACID_FIELD_SIZE = 256;
// Not a whole number: the front and back panels sit about half a texture apart
// in UV space, so an integer repeat would give both sides the same pattern.
const ACID_SCALE = 1.37;
const ACID_LOW = 0.45;
const ACID_HIGH = 0.7;
const ACID_VEIN = 0.06;
const VIDEO_MAX_SIZE = 1920;
// Draco decoder (WebAssembly) for compressed models, same Three.js release as the imports above;
// loaded only when a model uses Draco
const DRACO_DECODER_PATH = 'https://cdn.jsdelivr.net/npm/three@0.167.1/examples/jsm/libs/draco/gltf/';
// Models larger than this aren't downloaded in the background (see preloadModels)
const MAX_PRELOAD_BYTES = 15 * 1024 * 1024;

// Seamless marbled noise for acid wash: tileable multi-octave value noise,
// domain-warped so the bleach forms swirls rather than blobs. Values 0..1.
function createAcidField(size = ACID_FIELD_SIZE) {
  let seed = 1337;
  const random = () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const octave = (period) => {
    const grid = Float32Array.from({ length: period * period }, random);
    return (x, y) => {
      const gx = (((x % 1) + 1) % 1) * period;
      const gy = (((y % 1) + 1) % 1) * period;
      const x0 = Math.floor(gx);
      const y0 = Math.floor(gy);
      const x1 = (x0 + 1) % period;
      const y1 = (y0 + 1) % period;
      const fx = gx - x0;
      const fy = gy - y0;
      const sx = fx * fx * (3 - 2 * fx);
      const sy = fy * fy * (3 - 2 * fy);
      const a = grid[y0 * period + x0] + (grid[y0 * period + x1] - grid[y0 * period + x0]) * sx;
      const b = grid[y1 * period + x0] + (grid[y1 * period + x1] - grid[y1 * period + x0]) * sx;
      return a + (b - a) * sy;
    };
  };
  const octaves = [4, 8, 16, 32].map(octave);
  const fbm = (x, y) => {
    let value = 0;
    let amplitude = 0.5;
    let total = 0;
    octaves.forEach((sample) => {
      value += amplitude * sample(x, y);
      total += amplitude;
      amplitude *= 0.5;
    });
    return value / total;
  };
  const data = new Float32Array(size * size);
  for (let j = 0; j < size; j += 1) {
    for (let i = 0; i < size; i += 1) {
      const x = i / size;
      const y = j / size;
      const wx = fbm(x + 0.31, y + 0.17) - 0.5;
      const wy = fbm(x + 0.73, y + 0.51) - 0.5;
      const n = fbm(x + wx * 0.45, y + wy * 0.45);
      data[j * size + i] = Math.min(1, Math.max(0, (n - 0.5) * 2.4 + 0.5));
    }
  }
  return { size, data };
}

// Bilinear, wrapping lookup that matches a LinearFilter + RepeatWrapping sampler.
function sampleAcidField(field, u, v) {
  const { size, data } = field;
  const x = (((u % 1) + 1) % 1) * size - 0.5;
  const y = (((v % 1) + 1) % 1) * size - 0.5;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const i0 = (x0 + size) % size;
  const i1 = (x0 + 1 + size) % size;
  const row0 = ((y0 + size) % size) * size;
  const row1 = ((y0 + 1 + size) % size) * size;
  const a = data[row0 + i0] + (data[row0 + i1] - data[row0 + i0]) * fx;
  const b = data[row1 + i0] + (data[row1 + i1] - data[row1 + i0]) * fx;
  return a + (b - a) * fy;
}

const smoothstep = (lo, hi, x) => {
  const t = Math.min(1, Math.max(0, (x - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
};

// JS twin of the acid-wash block in the fragment shader (linear RGB in/out).
// Writes into `out` instead of allocating: the GLB bake calls it per texel.
const ACID_TINT = [1, 0.97, 0.93];
function acidWashPixel(r, g, b, n, amount, out) {
  const bleach = smoothstep(ACID_LOW, ACID_HIGH, n) * amount;
  const vein = 1 - smoothstep(0, ACID_VEIN, Math.abs(n - ACID_LOW));
  const faded = (r * 0.299 + g * 0.587 + b * 0.114) * 0.45 + 0.55;
  const darken = 1 - vein * 0.25 * amount;
  out[0] = (r + (faded * ACID_TINT[0] - r) * bleach) * darken;
  out[1] = (g + (faded * ACID_TINT[1] - g) * bleach) * darken;
  out[2] = (b + (faded * ACID_TINT[2] - b) * bleach) * darken;
  return out;
}

function mapTriangle(ctx, image, source, target, expand = 0) {
  const [s0, s1, s2] = source;
  const [t0, t1, t2] = target;
  const denominator = s0.x * (s1.y - s2.y) + s1.x * (s2.y - s0.y) + s2.x * (s0.y - s1.y);
  if (Math.abs(denominator) < 0.00001) return;

  const affine = (key) => [
    (t0[key] * (s1.y - s2.y) + t1[key] * (s2.y - s0.y) + t2[key] * (s0.y - s1.y)) / denominator,
    (t0[key] * (s2.x - s1.x) + t1[key] * (s0.x - s2.x) + t2[key] * (s1.x - s0.x)) / denominator,
    (t0[key] * (s1.x * s2.y - s2.x * s1.y) + t1[key] * (s2.x * s0.y - s0.x * s2.y) + t2[key] * (s0.x * s1.y - s1.x * s0.y)) / denominator,
  ];
  const [a, c, e] = affine('x');
  const [b, d, f] = affine('y');

  // Growing the clip slightly past each edge hides the anti-aliased hairlines
  // that would otherwise appear between neighbouring triangles.
  const cx = (t0.x + t1.x + t2.x) / 3;
  const cy = (t0.y + t1.y + t2.y) / 3;
  ctx.save();
  ctx.beginPath();
  [t0, t1, t2].forEach((point, i) => {
    const dx = point.x - cx;
    const dy = point.y - cy;
    const length = Math.hypot(dx, dy) || 1;
    const x = point.x + (dx / length) * expand;
    const y = point.y + (dy / length) * expand;
    if (i) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  });
  ctx.closePath();
  ctx.clip();
  ctx.transform(a, b, c, d, e, f);
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function hexToColor(hex, fallback = '#ffffff') {
  try {
    return new THREE.Color(hex || fallback);
  } catch {
    return new THREE.Color(fallback);
  }
}

// Copies an image into a canvas no larger than `max` on its longest side, so
// per-frame drawing never has to downscale a huge photo.
function fitImage(source, max) {
  const width = source.naturalWidth || source.width || 512;
  const height = source.naturalHeight || source.height || 512;
  const scale = Math.min(1, max / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function panelInnerRect(name) {
  const panel = BOARD_PANELS[name] || BOARD_PANELS.front;
  return {
    x: panel.x + PANEL_PADDING.side,
    y: panel.y + PANEL_PADDING.top,
    width: panel.width - PANEL_PADDING.side * 2,
    height: panel.height - PANEL_PADDING.top - PANEL_PADDING.bottom,
  };
}

class VTConfigurator {
  constructor(root) {
    this.root = root;
    this.modelUrl = root.dataset.modelUrl;
    // Product types (assets/custom-product-types.json): model, sizes, price and placement of each
    this.typesUrl = root.dataset.typesUrl;
    this.types = [];
    this.type = null;
    this.size = null;
    // Sizes the customer picked, latest first: a type without the last one (a kids' tee after M)
    // doesn't lose it for the next type (a hoodie: M again)
    this.sizeHistory = [];
    this.quantity = 1;
    // 'chest': a garment, the design on the chest; 'wrap': a cup, the design around its side
    this.placementMode = 'chest';
    // Buy now: the App Proxy of the Mockup app's backend, under the store's (locale) root
    this.rootUrl = (root.dataset.rootUrl || '/').replace(/\/?$/, '/');
    this.buyUrl = `${this.rootUrl.replace(/\/$/, '')}${root.dataset.buyUrl || '/apps/designer'}`.replace(/\/$/, '');
    this.turnstileKey = root.dataset.turnstileKey || null;
    this.buying = false;
    // Chest placement (no position guide): an uploaded design is fitted into a print area of the front
    // piece and centred, like the Mockup app's default placement; fractions of the front piece
    this.chestPlacement = root.dataset.placement === 'chest';
    const percent = (value, fallback) => (Number.isFinite(Number(value)) && value !== '' ? Number(value) : fallback) / 100;
    this.printArea = {
      top: percent(root.dataset.printTop, 13),
      width: percent(root.dataset.printWidth, 86),
      height: percent(root.dataset.printHeight, 49),
      // Highest point a design may reach when enlarged (just below the neckline / hood)
      limitTop: percent(root.dataset.printLimitTop, 0),
    };
    this.boardScale = this.chestPlacement ? CHEST_BOARD_SCALE : BOARD_SCALE;
    this.layers = [];
    this.selectedId = null;
    this.activePanel = 'front';
    this.drawing = false;
    this.stroke = null;
    this.drag = null;
    this.renderFrame = 0;
    this.boardDirty = true;
    this.guideDirty = true;
    this.silhouetteDirty = true;
    this.anim = 'static';
    this.animSpeed = 0.5;
    this.camSpin = false;
    this.camZoom = false;
    this.acid = 0;
    this.puff = 0;
    this.garmentHex = '#ffffff';
    this.bgHex = '#111111';
    this.clock = new THREE.Clock();
    // The section's "highest point" setting, for product types without their own
    this.sectionLimitTop = this.printArea.limitTop || 0;
    this.initBoard();
    this.bindUi();
    this.initThree();
    this.initProductTypes();
  }

  $(sel) {
    return this.root.querySelector(sel);
  }

  $all(sel) {
    return [...this.root.querySelectorAll(sel)];
  }

  // The Customize button on the storefront passes ?return_to=<path#section>.
  // Only same-site paths are accepted so the link can't send visitors elsewhere.
  initBackLink() {
    const back = this.$('[data-vt-back]');
    if (!back) return;
    const returnTo = new URLSearchParams(window.location.search).get('return_to');
    if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.includes('\\')) return;
    const target = new URL(returnTo, window.location.origin);
    if (target.origin === window.location.origin) back.href = target.pathname + target.search + target.hash;
  }

  bindUi() {
    this.initBackLink();
    this.$all('[data-vt-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => this.togglePanel(btn.dataset.vtToggle));
    });
    this.$all('[data-vt-open-edit]').forEach((b) => b.addEventListener('click', () => this.toggleEdit(true)));
    this.$('[data-vt-close-edit]')?.addEventListener('click', () => this.toggleEdit(false));
    this.$('[data-vt-menu]')?.addEventListener('click', () => {
      const panel = this.$('[data-vt-menu-panel]');
      panel.hidden = !panel.hidden;
    });
    this.$('[data-vt-fullscreen]')?.addEventListener('click', () => this.toggleFullscreen());

    const garmentColor = this.$('[data-vt-garment-color]');
    const garmentHex = this.$('[data-vt-garment-hex]');
    garmentColor?.addEventListener('input', () => {
      garmentHex.value = garmentColor.value;
      this.setGarmentColor(garmentColor.value);
    });
    garmentHex?.addEventListener('change', () => {
      if (!/^#[0-9a-f]{6}$/i.test(garmentHex.value)) {
        garmentHex.value = garmentColor.value;
        return;
      }
      garmentColor.value = garmentHex.value.toLowerCase();
      this.setGarmentColor(garmentHex.value);
    });
    this.$('[data-vt-color-swatches]')?.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-vt-swatch]');
      if (swatch) this.setGarmentColor(swatch.dataset.vtSwatch);
    });

    // Size guide (the type's style_spec chart), in/cm like the product page (same remembered unit)
    const sizeDialog = this.$('[data-vt-size-dialog]');
    this.$('[data-vt-size-guide]')?.addEventListener('click', () => {
      this.syncSizeGuide();
      sizeDialog?.showModal();
    });
    sizeDialog?.addEventListener('click', (e) => {
      if (e.target === sizeDialog || e.target.closest('[data-vt-size-close]')) sizeDialog.close();
      const unit = e.target.closest('[data-unit-button]')?.dataset.unitButton;
      if (unit) this.setChartUnit(unit);
    });
    try {
      const saved = window.localStorage.getItem('size-chart-unit');
      if (saved === 'in' || saved === 'cm') this.setChartUnit(saved, false);
    } catch {
      // Storage unavailable: the default unit
    }

    const bgColor = this.$('[data-vt-bg-color]');
    const bgHex = this.$('[data-vt-bg-hex]');
    bgColor?.addEventListener('input', () => {
      bgHex.value = bgColor.value;
      this.setBackground(bgColor.value);
    });
    bgHex?.addEventListener('change', () => {
      bgColor.value = bgHex.value;
      this.setBackground(bgHex.value);
    });
    this.$('[data-vt-bg-upload]')?.addEventListener('click', () => this.$('[data-vt-bg-file]').click());
    this.$('[data-vt-bg-file]')?.addEventListener('change', (e) => this.onBackgroundUpload(e.target.files[0]));
    this.$('[data-vt-bg-clear]')?.addEventListener('click', () => this.setBackground(this.bgHex));

    this.$all('[data-vt-anim]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.anim = btn.dataset.vtAnim;
        this.$all('[data-vt-anim]').forEach((b) => b.classList.toggle('is-active', b === btn));
      });
    });
    this.$('[data-vt-anim-speed]')?.addEventListener('input', (e) => {
      this.animSpeed = Number(e.target.value) / 100;
    });
    this.$('[data-vt-cam-spin]')?.addEventListener('change', (e) => {
      this.camSpin = e.target.checked;
      if (this.controls) this.controls.autoRotate = this.camSpin;
    });
    this.$('[data-vt-cam-zoom]')?.addEventListener('change', (e) => {
      this.camZoom = e.target.checked;
      // The zoom animation owns the camera distance while it runs; wheel/pinch
      // zoom is paused and the original distance is restored afterwards.
      if (this.camZoom) this.zoomBaseDistance = this.cameraDistance();
      else if (this.zoomBaseDistance) this.setCameraDistance(this.zoomBaseDistance);
      if (this.controls) this.controls.enableZoom = !this.camZoom;
    });

    const acid = this.$('[data-vt-acid]');
    acid?.addEventListener('input', () => {
      this.acid = Number(acid.value) / 100;
      this.$('[data-vt-acid-value]').textContent = this.acid.toFixed(1);
      this.syncTint();
    });
    const puff = this.$('[data-vt-puff]');
    puff?.addEventListener('input', () => {
      this.puff = Number(puff.value) / 100;
      this.$('[data-vt-puff-value]').textContent = this.puff.toFixed(1);
      this.syncTint();
    });

    this.$('[data-vt-draw]')?.addEventListener('click', () => this.setDrawing(!this.drawing));
    this.$('[data-vt-upload]')?.addEventListener('click', () => this.$('[data-vt-file]').click());
    this.$('[data-vt-file]')?.addEventListener('change', (e) => this.onUpload(e.target.files[0]));
    this.$('[data-vt-remove-bg]')?.addEventListener('click', () => this.editSelectedBackground());
    this.$('[data-vt-add-text]')?.addEventListener('click', () => this.addText());
    this.$('[data-vt-save]')?.addEventListener('click', () => this.saveLayout());
    this.$('[data-vt-load]')?.addEventListener('click', () => this.loadLayout());
    this.$('[data-vt-reset]')?.addEventListener('click', () => this.resetDesigns());
    this.$('[data-vt-design-size]')?.addEventListener('input', (e) => this.setDesignSize(Number(e.target.value) / 100));
    this.$('[data-vt-type]')?.addEventListener('change', (e) => this.selectType(e.target.value));
    this.$('[data-vt-sizes]')?.addEventListener('click', (e) => {
      const button = e.target.closest('[data-vt-size]');
      if (!button) return;
      const size = button.dataset.vtSize;
      this.sizeHistory = [size, ...this.sizeHistory.filter((item) => item !== size)].slice(0, 6);
      this.selectSize(size);
    });
    this.$all('[data-vt-qty-step]').forEach((button) => {
      button.addEventListener('click', () => this.setQuantity(this.quantity + Number(button.dataset.vtQtyStep)));
    });
    this.$('[data-vt-qty]')?.addEventListener('change', (e) => this.setQuantity(Number(e.target.value)));
    this.$('[data-vt-design-name]')?.addEventListener('input', () => this.syncBuyButton());
    this.$('[data-vt-buy]')?.addEventListener('click', () => this.buyNow());
    this.$all('[data-vt-export]').forEach((btn) => {
      btn.addEventListener('click', () => this.exportFile(btn.dataset.vtExport));
    });
    const videoFormat = this.$('[data-vt-video-format]');
    if (videoFormat) {
      const mime = VTConfigurator.pickVideoMime();
      videoFormat.textContent = !mime ? 'Video export is not supported in this browser'
        : `Saves as ${mime.startsWith('video/mp4') ? 'MP4' : 'WebM'}`;
    }

    // Text controls edit the selected text layer, or set defaults for the next one.
    this.$('[data-vt-text-value]')?.addEventListener('input', (e) => {
      this.updateSelectedText({ text: e.target.value });
    });
    this.$('[data-vt-text-color]')?.addEventListener('input', (e) => {
      this.updateSelectedText({ color: e.target.value });
    });
    this.$('[data-vt-font-size]')?.addEventListener('input', (e) => {
      const size = Number(e.target.value);
      if (size > 0) this.updateSelectedText({ h: size * TEXT_LINE_HEIGHT });
    });
    this.$('[data-vt-font]')?.addEventListener('change', (e) => {
      this.updateSelectedText({ font: e.target.value });
      this.loadFont(e.target.value);
    });

    const guide = this.guideCanvas;
    guide?.addEventListener('pointerdown', (event) => this.onGuidePointerDown(event));
    guide?.addEventListener('pointermove', (event) => this.onGuidePointerMove(event));
    guide?.addEventListener('pointerup', (event) => this.onGuidePointerUp(event));
    guide?.addEventListener('pointercancel', (event) => this.onGuidePointerUp(event));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (!this.selectedId || this.$('[data-vt-edit-panel]')?.hidden) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable]')) return;
      event.preventDefault();
      this.deleteSelected();
    });
  }

  togglePanel(name) {
    const target = this.$(`[data-vt-panel="${name}"]`);
    if (!target) return;
    const open = target.hidden;
    const accordion = ['product', 'color', 'background', 'animation', 'camera'];
    this.$all('[data-vt-panel]').forEach((el) => {
      if (el === target) return;
      if (accordion.includes(name) && accordion.includes(el.dataset.vtPanel)) el.hidden = true;
      if (name === 'export' && el.dataset.vtPanel === 'export') el.hidden = true;
    });
    target.hidden = !open;
    this.syncMobileSheet();
  }

  // Phones (3d-configuration.css, max-width 860px): the toolbar is a sheet shown only while one of
  // its panels is open from the bottom tabs, so it no longer covers the garment all the time
  syncMobileSheet() {
    const sheetPanels = ['product', 'color', 'background', 'animation', 'camera', 'advanced'];
    const isOpen = (name) => this.$(`[data-vt-panel="${name}"]`)?.hidden === false;
    this.root.classList.toggle('is-sheet-open', sheetPanels.some(isOpen));
    this.$all('[data-vt-mobile-tabs] [data-vt-toggle]').forEach((tab) =>
      tab.setAttribute('aria-pressed', String(isOpen(tab.dataset.vtToggle)))
    );
  }

  toggleEdit(open) {
    const panel = this.$('[data-vt-edit-panel]');
    const btn = this.$('[data-vt-open-edit]');
    const next = open ?? panel.hidden;
    panel.hidden = !next;
    btn?.classList.toggle('is-active', next);
    if (next) this.resizeGuide();
  }

  toggleFullscreen() {
    this.root.classList.toggle('is-fullscreen');
    this.resize();
  }

  setDrawing(on) {
    this.drawing = on;
    const drawButton = this.$('[data-vt-draw]');
    drawButton?.classList.toggle('is-active', on);
    drawButton?.setAttribute('aria-pressed', String(on));
    if (on) this.select(null);
    if (this.guideCanvas) this.guideCanvas.style.cursor = on ? 'crosshair' : 'default';
  }

  // ---------------------------------------------------------------------------
  // Board: layers are composited here once per frame. The same canvas is the
  // texture sampled by the garment shader and the image shown in the guide.
  // ---------------------------------------------------------------------------

  initBoard() {
    this.boardCanvas = document.createElement('canvas');
    this.boardCanvas.width = Math.round(BOARD_W * this.boardScale);
    this.boardCanvas.height = Math.round(BOARD_H * this.boardScale);
    this.boardCtx = this.boardCanvas.getContext('2d');
    this.boardTex = new THREE.CanvasTexture(this.boardCanvas);
    this.boardTex.colorSpace = THREE.SRGBColorSpace;
    this.boardTex.flipY = false;
    // Premultiplied alpha avoids dark fringes around transparent design edges.
    this.boardTex.premultiplyAlpha = true;
    // Mipmaps would be regenerated on every drag frame; the board is only ever
    // mildly minified on screen, so plain linear filtering is enough.
    this.boardTex.generateMipmaps = false;
    this.boardTex.minFilter = THREE.LinearFilter;

    // Acid wash noise. Values are quantised to bytes here so the GLB bake reads
    // exactly what the GPU samples.
    this.acidField = createAcidField();
    const { size, data } = this.acidField;
    const bytes = new Uint8Array(size * size * 4);
    data.forEach((n, i) => {
      const b = Math.round(n * 255);
      data[i] = b / 255;
      bytes[i * 4] = b;
      bytes[i * 4 + 1] = b;
      bytes[i * 4 + 2] = b;
      bytes[i * 4 + 3] = 255;
    });
    this.acidTexture = new THREE.DataTexture(bytes, size, size);
    this.acidTexture.wrapS = THREE.RepeatWrapping;
    this.acidTexture.wrapT = THREE.RepeatWrapping;
    this.acidTexture.magFilter = THREE.LinearFilter;
    this.acidTexture.minFilter = THREE.LinearMipmapLinearFilter;
    this.acidTexture.generateMipmaps = true;
    this.acidTexture.needsUpdate = true;

    this.measureCtx = document.createElement('canvas').getContext('2d');
    this.silhouetteFill = document.createElement('canvas');
    this.silhouetteOutline = document.createElement('canvas');
    this.guideCanvas = this.$('[data-vt-guide]');
    this.guideCtx = this.guideCanvas?.getContext('2d');
    if (this.guideCanvas && typeof ResizeObserver !== 'undefined') {
      this.guideResizeObserver = new ResizeObserver(() => this.resizeGuide());
      this.guideResizeObserver.observe(this.guideCanvas);
    }
  }

  resizeGuide() {
    const canvas = this.guideCanvas;
    if (!canvas || !canvas.clientWidth) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.round(canvas.clientWidth * ratio);
    const height = Math.round((width * BOARD_H) / BOARD_W);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      this.silhouetteDirty = true;
    }
    this.requestRender();
  }

  // Coalesces any number of changes into one board/guide redraw per frame.
  requestRender({ board = false } = {}) {
    if (board) this.boardDirty = true;
    this.guideDirty = true;
    if (this.renderFrame) return;
    this.renderFrame = requestAnimationFrame(() => {
      this.renderFrame = 0;
      if (this.boardDirty) this.renderBoard();
      if (this.guideDirty) this.renderGuide();
    });
  }

  renderBoard() {
    const ctx = this.boardCtx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.boardCanvas.width, this.boardCanvas.height);
    ctx.setTransform(this.boardScale, 0, 0, this.boardScale, 0, 0);
    this.layers.forEach((layer) => this.drawLayer(ctx, layer));
    this.boardTex.needsUpdate = true;
    this.boardDirty = false;
  }

  // Puff print is a relief in the garment shader, so layers are drawn flat here.
  drawLayer(ctx, layer) {
    ctx.save();
    if (layer.kind === 'stroke') {
      ctx.strokeStyle = layer.color;
      ctx.lineWidth = layer.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      layer.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      if (layer.points.length === 1) ctx.lineTo(layer.points[0].x, layer.points[0].y);
      ctx.stroke();
    } else if (layer.image) {
      ctx.translate(layer.x, layer.y);
      ctx.rotate(layer.rotation || 0);
      ctx.drawImage(layer.image, -layer.w / 2, -layer.h / 2, layer.w, layer.h);
    }
    ctx.restore();
  }

  renderGuide() {
    const canvas = this.guideCanvas;
    const ctx = this.guideCtx;
    if (!canvas || !ctx || !canvas.clientWidth) return;
    this.guideDirty = false;
    if (this.silhouetteDirty) this.renderSilhouettes();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.silhouetteFill, 0, 0);
    ctx.drawImage(this.boardCanvas, 0, 0, canvas.width, canvas.height);
    // Piece outlines sit above the designs so edges stay visible under artwork.
    ctx.drawImage(this.silhouetteOutline, 0, 0);
    const selected = this.selectedLayer();
    if (selected) this.drawSelection(ctx, selected, canvas.width / BOARD_W);
  }

  renderSilhouettes() {
    const canvas = this.guideCanvas;
    const s = canvas.width / BOARD_W;
    const pixelRatio = canvas.width / canvas.clientWidth;
    [this.silhouetteFill, this.silhouetteOutline].forEach((c) => {
      c.width = canvas.width;
      c.height = canvas.height;
    });

    const fill = this.silhouetteFill.getContext('2d');
    fill.setTransform(s, 0, 0, s, 0, 0);
    const labelSize = (10 * pixelRatio) / s;
    Object.values(BOARD_PANELS).forEach((panel) => {
      fill.fillStyle = '#fafafa';
      fill.beginPath();
      fill.roundRect(panel.x, panel.y, panel.width, panel.height, 10);
      fill.fill();
      fill.fillStyle = GUIDE_LABEL_COLOR;
      fill.font = `700 ${labelSize}px Inter, Arial, sans-serif`;
      fill.textBaseline = 'top';
      fill.fillText(panel.label, panel.x + 10, panel.y + 9);
    });

    this.silhouettePaths ||= this.buildSilhouettePaths();
    const paths = this.silhouettePaths;
    fill.fillStyle = '#e9e8e5';
    paths.forEach((path) => fill.fill(path));

    // Outline = silhouette dilated by a pixel or two, minus the silhouette itself.
    const outline = this.silhouetteOutline.getContext('2d');
    const r = 1.25 * pixelRatio;
    outline.fillStyle = 'rgba(120, 120, 128, 0.55)';
    [[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1]].forEach(([dx, dy]) => {
      outline.setTransform(s, 0, 0, s, dx * r, dy * r);
      paths.forEach((path) => outline.fill(path));
    });
    outline.globalCompositeOperation = 'destination-out';
    outline.setTransform(s, 0, 0, s, 0, 0);
    outline.fillStyle = '#000';
    paths.forEach((path) => outline.fill(path));
    outline.globalCompositeOperation = 'source-over';

    // Centre-front / centre-back ticks on the collar rows, drawn above artwork.
    const px = pixelRatio / s;
    outline.strokeStyle = GUIDE_LABEL_COLOR;
    outline.fillStyle = GUIDE_LABEL_COLOR;
    outline.lineWidth = px;
    outline.setLineDash([3 * px, 3 * px]);
    outline.font = `600 ${9 * px}px Inter, Arial, sans-serif`;
    outline.textAlign = 'center';
    outline.textBaseline = 'bottom';
    (this.collarMarks || []).forEach((mark) => {
      outline.beginPath();
      outline.moveTo(mark.x, mark.top - 3 * px);
      outline.lineTo(mark.x, mark.bottom + 3 * px);
      outline.stroke();
      outline.fillText(mark.label, mark.x, mark.top - 4 * px);
    });
    outline.setLineDash([]);
    this.silhouetteDirty = false;
  }

  drawSelection(ctx, layer, s) {
    if (layer.kind === 'stroke') return;
    const px = this.unitsPerPx();
    const handle = 9 * px;
    const rotate = this.rotateHandleLocal(layer);
    ctx.save();
    ctx.setTransform(s, 0, 0, s, 0, 0);
    ctx.translate(layer.x, layer.y);
    ctx.rotate(layer.rotation || 0);
    ctx.strokeStyle = '#ff501b';
    ctx.lineWidth = 1.5 * px;
    ctx.strokeRect(-layer.w / 2, -layer.h / 2, layer.w, layer.h);
    ctx.fillStyle = '#fff';
    CORNER_SIGNS.forEach(({ x, y }) => {
      ctx.beginPath();
      ctx.rect((x * layer.w) / 2 - handle / 2, (y * layer.h) / 2 - handle / 2, handle, handle);
      ctx.fill();
      ctx.stroke();
    });
    ctx.beginPath();
    ctx.moveTo(layer.w / 2, -layer.h / 2);
    ctx.lineTo(rotate.x, rotate.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(rotate.x, rotate.y, 6 * px, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Guide interaction
  // ---------------------------------------------------------------------------

  unitsPerPx() {
    return BOARD_W / Math.max(this.guideCanvas?.clientWidth || 0, 1);
  }

  boardPoint(event) {
    const rect = this.guideCanvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * BOARD_W,
      y: ((event.clientY - rect.top) / rect.height) * BOARD_H,
    };
  }

  panelAt(point) {
    return Object.keys(BOARD_PANELS).find((name) => {
      const p = BOARD_PANELS[name];
      return point.x >= p.x && point.x <= p.x + p.width && point.y >= p.y && point.y <= p.y + p.height;
    }) || null;
  }

  selectedLayer() {
    return this.layers.find((layer) => layer.id === this.selectedId) || null;
  }

  select(id) {
    this.selectedId = id;
    this.syncTextControls();
    // "Remove background" applies to an uploaded design that still has its original pixels
    const removeBg = this.$('[data-vt-remove-bg]');
    if (removeBg) {
      const layer = this.layers.find((item) => item.id === id);
      removeBg.hidden = !(layer && layer.kind === 'image' && layer.source);
    }
    this.requestRender();
  }

  layerLocalPoint(layer, point) {
    const dx = point.x - layer.x;
    const dy = point.y - layer.y;
    const rotation = layer.rotation || 0;
    return {
      x: Math.cos(rotation) * dx + Math.sin(rotation) * dy,
      y: -Math.sin(rotation) * dx + Math.cos(rotation) * dy,
    };
  }

  layerBoardPoint(layer, localX, localY) {
    const rotation = layer.rotation || 0;
    return {
      x: layer.x + Math.cos(rotation) * localX - Math.sin(rotation) * localY,
      y: layer.y + Math.sin(rotation) * localX + Math.cos(rotation) * localY,
    };
  }

  rotateHandleLocal(layer) {
    const offset = 16 * this.unitsPerPx();
    return { x: layer.w / 2 + offset, y: -layer.h / 2 - offset };
  }

  hitTest(point) {
    const tolerance = 10 * this.unitsPerPx();
    const near = (p) => Math.hypot(point.x - p.x, point.y - p.y) < tolerance;
    const selected = this.selectedLayer();
    if (selected && selected.kind !== 'stroke') {
      const rotate = this.rotateHandleLocal(selected);
      if (near(this.layerBoardPoint(selected, rotate.x, rotate.y))) return { layer: selected, mode: 'rotate' };
      const sign = CORNER_SIGNS.find(({ x, y }) => near(
        this.layerBoardPoint(selected, (x * selected.w) / 2, (y * selected.h) / 2)
      ));
      if (sign) return { layer: selected, mode: 'resize', sign };
    }
    for (let i = this.layers.length - 1; i >= 0; i -= 1) {
      const layer = this.layers[i];
      if (layer.kind === 'stroke') continue;
      const local = this.layerLocalPoint(layer, point);
      if (Math.abs(local.x) <= layer.w / 2 && Math.abs(local.y) <= layer.h / 2) return { layer, mode: 'move' };
    }
    return null;
  }

  onGuidePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const point = this.boardPoint(event);
    this.guideCanvas.setPointerCapture?.(event.pointerId);
    this.activePanel = this.panelAt(point) || this.activePanel;

    if (this.drawing) {
      this.stroke = {
        id: uid(),
        kind: 'stroke',
        color: this.$('[data-vt-stroke-color]').value,
        width: Number(this.$('[data-vt-stroke-width]').value || 4),
        points: [point],
      };
      this.layers.push(this.stroke);
      this.requestRender({ board: true });
      return;
    }

    const hit = this.hitTest(point);
    this.select(hit?.layer.id || null);
    if (!hit) {
      this.drag = null;
      return;
    }
    const { layer } = hit;
    if (hit.mode === 'rotate') {
      this.drag = { layer, mode: 'rotate', lastAngle: Math.atan2(point.y - layer.y, point.x - layer.x) };
    } else if (hit.mode === 'resize') {
      this.drag = {
        layer,
        mode: 'resize',
        signX: hit.sign.x,
        signY: hit.sign.y,
        anchor: this.layerBoardPoint(layer, (-hit.sign.x * layer.w) / 2, (-hit.sign.y * layer.h) / 2),
        startW: layer.w,
        startH: layer.h,
      };
    } else {
      this.drag = { layer, mode: 'move', last: point };
    }
  }

  onGuidePointerMove(event) {
    const point = this.boardPoint(event);
    if (this.stroke) {
      const last = this.stroke.points[this.stroke.points.length - 1];
      if (Math.hypot(point.x - last.x, point.y - last.y) < 0.75) return;
      this.stroke.points.push(point);
      this.requestRender({ board: true });
      return;
    }
    if (!this.drag) {
      this.updateGuideCursor(point);
      return;
    }

    const { layer } = this.drag;
    if (this.drag.mode === 'rotate') {
      const angle = Math.atan2(point.y - layer.y, point.x - layer.x);
      const delta = Math.atan2(Math.sin(angle - this.drag.lastAngle), Math.cos(angle - this.drag.lastAngle));
      layer.rotation = (layer.rotation || 0) + delta;
      this.drag.lastAngle = angle;
    } else if (this.drag.mode === 'resize') {
      const { anchor, signX, signY, startW, startH } = this.drag;
      const dx = point.x - anchor.x;
      const dy = point.y - anchor.y;
      const rotation = layer.rotation || 0;
      const localX = Math.cos(rotation) * dx + Math.sin(rotation) * dy;
      const localY = -Math.sin(rotation) * dx + Math.cos(rotation) * dy;
      const projectedScale = (signX * localX * startW + signY * localY * startH) / (startW ** 2 + startH ** 2);
      layer.w = Math.max(8, startW * projectedScale);
      layer.h = (layer.w * startH) / startW;
      layer.x = anchor.x + Math.cos(rotation) * signX * layer.w / 2 - Math.sin(rotation) * signY * layer.h / 2;
      layer.y = anchor.y + Math.sin(rotation) * signX * layer.w / 2 + Math.cos(rotation) * signY * layer.h / 2;
      if (layer.kind === 'text') this.syncTextControls();
    } else {
      layer.x += point.x - this.drag.last.x;
      layer.y += point.y - this.drag.last.y;
      this.drag.last = point;
    }
    this.requestRender({ board: true });
  }

  onGuidePointerUp(event) {
    if (this.guideCanvas?.hasPointerCapture?.(event.pointerId)) {
      this.guideCanvas.releasePointerCapture(event.pointerId);
    }
    this.stroke = null;
    this.drag = null;
  }

  updateGuideCursor(point) {
    if (this.drawing) {
      this.guideCanvas.style.cursor = 'crosshair';
      return;
    }
    const hit = this.hitTest(point);
    let cursor = 'default';
    if (hit?.mode === 'move') cursor = 'move';
    else if (hit?.mode === 'rotate') cursor = 'grab';
    else if (hit?.mode === 'resize') cursor = hit.sign.x * hit.sign.y > 0 ? 'nwse-resize' : 'nesw-resize';
    this.guideCanvas.style.cursor = cursor;
  }

  // ---------------------------------------------------------------------------
  // Layers
  // ---------------------------------------------------------------------------

  // Background removal module (3d-background-removal.js), loaded the first time it is needed.
  // null when it can't load: uploads then work as before, without background removal.
  loadBackgroundRemoval() {
    const url = this.root.dataset.bgRemovalUrl;
    if (!url) return Promise.resolve(null);
    this.bgRemoval ??= import(url).catch((error) => {
      console.error(error);
      this.bgRemoval = null;
      return null;
    });
    return this.bgRemoval;
  }

  // Opens the background removal dialog; resolves its outcome, or null when unavailable/cancelled
  async openBackgroundEditor(source, analysis, initial, title = 'Upload design') {
    const module = await this.loadBackgroundRemoval();
    const dialog = this.$('[data-vt-bg-dialog]');
    if (!module || !dialog) return null;
    const heading = dialog.querySelector('.vt-bg-dialog__title');
    if (heading) heading.textContent = title;
    return module.editBackground(dialog, { source, analysis, initial, garmentColor: this.garmentHex });
  }

  // "Remove background" for the selected uploaded design, from its original pixels
  async editSelectedBackground() {
    const layer = this.layers.find((item) => item.id === this.selectedId);
    if (!layer || layer.kind !== 'image' || !layer.source) return;
    const outcome = await this.openBackgroundEditor(layer.source, layer.bgAnalysis, layer.bgOptions, 'Remove background');
    if (!outcome) return;
    // Same pixel size as the source, so the layer keeps its size and position
    if (outcome.action === 'apply') {
      layer.image = outcome.canvas;
      layer.bgOptions = outcome.options;
    } else {
      layer.image = layer.source;
      layer.bgOptions = null;
    }
    this.requestRender({ board: true });
  }

  async onUpload(file) {
    if (!file) return;
    this.setDrawing(false);
    try {
      // The original, at most MAX_LAYER_IMAGE on its longest side: background removal works on it
      const source = fitImage(await this.readImage(file), MAX_LAYER_IMAGE);
      let image = source;
      let bgAnalysis = null;
      let bgOptions = null;
      const bgRemoval = await this.loadBackgroundRemoval();
      if (bgRemoval) {
        bgAnalysis = bgRemoval.analyzeBackground(source);
        // A design without transparency would print as a solid rectangle: offer to remove its background
        if (!bgAnalysis.hasTransparency) {
          const outcome = await this.openBackgroundEditor(source, bgAnalysis, null);
          if (!outcome) {
            this.$('[data-vt-file]').value = '';
            return;
          }
          if (outcome.action === 'apply') {
            image = outcome.canvas;
            bgOptions = outcome.options;
          }
        }
      }
      if (this.chestPlacement) {
        this.placeChestDesign({ image, source: bgRemoval ? source : null, bgAnalysis, bgOptions });
        this.$('[data-vt-file]').value = '';
        return;
      }
      const area = panelInnerRect(this.activePanel);
      const scale = Math.min((area.width * 0.6) / image.width, (area.height * 0.6) / image.height);
      const layer = {
        id: uid(),
        kind: 'image',
        image,
        // Kept to redo the background removal later ("Remove background"); not saved in layouts
        source: bgRemoval ? source : null,
        bgAnalysis,
        bgOptions,
        x: area.x + area.width / 2,
        y: area.y + area.height / 2,
        w: image.width * scale,
        h: image.height * scale,
        rotation: 0,
      };
      this.layers.push(layer);
      this.select(layer.id);
      this.requestRender({ board: true });
    } catch (error) {
      console.error(error);
    }
    this.$('[data-vt-file]').value = '';
  }

  // ---------------------------------------------------------------------------
  // Chest placement (no position guide)
  // ---------------------------------------------------------------------------

  // The print area on the board: fractions of the front piece's bounds (known once the model is
  // projected; until then, of the FRONT panel)
  chestArea() {
    const piece = this.frontPieceRect || panelInnerRect('front');
    const { top, width, height } = this.printArea;
    // A cup's unrolled side is centred on the part facing the viewer
    const centerX = piece.centerX ?? piece.x + piece.width / 2;
    return {
      x: centerX - (piece.width * width) / 2,
      y: piece.y + piece.height * top,
      width: piece.width * width,
      height: piece.height * height,
    };
  }

  // Size and position of the chest design: fitted into the print area at size 1, then scaled around
  // the area's centre (Mockup app: designRect with offsets 0). The size stops where the design would
  // leave the front piece (it would be cut off at the sleeves and shoulders) or climb above the
  // print limit (into the neckline or hood), at most 1.5.
  fitChestLayer(layer) {
    const area = this.chestArea();
    const piece = this.frontPieceRect || panelInnerRect('front');
    const fit = Math.min(area.width / layer.image.width, area.height / layer.image.height);
    const baseW = layer.image.width * fit;
    const baseH = layer.image.height * fit;
    const cx = area.x + area.width / 2;
    const cy = area.y + area.height / 2;
    const limitY = Math.min(area.y, piece.y + piece.height * this.printArea.limitTop);
    const roomY = 2 * Math.min(cy - limitY, piece.y + piece.height - cy);
    // A cup: up to the handle, where the design would go under it (or most of the way round)
    const roomX = piece.wrapRoom ?? piece.width;
    layer.maxSize = Math.max(1, Math.min(this.maxDesignSize ?? 1.5, roomX / baseW, roomY / baseH));
    const size = Math.min(layer.size || 1, layer.maxSize);
    layer.w = baseW * size;
    layer.h = baseH * size;
    layer.x = cx;
    layer.y = cy;
    layer.rotation = 0;
    this.syncSizeSlider(layer, size);
  }

  // The slider's range follows the design (its largest size on the front piece)
  syncSizeSlider(layer, size) {
    const slider = this.$('[data-vt-design-size]');
    if (!slider) return;
    slider.max = String(Math.floor((layer?.maxSize ?? 1.5) * 100));
    slider.value = String(Math.round(size * 100));
    const label = this.$('[data-vt-design-size-value]');
    if (label) label.textContent = `${slider.value}%`;
  }

  // A new upload replaces the design: without the guide there is no way to pick or delete layers
  placeChestDesign({ image, source, bgAnalysis, bgOptions }) {
    const layer = {
      id: uid(),
      kind: 'image',
      image,
      // Kept to redo the background removal later ("Remove background"); not saved in layouts
      source,
      bgAnalysis,
      bgOptions,
      size: this.designSize ?? 1,
    };
    this.fitChestLayer(layer);
    this.layers = [layer];
    this.select(layer.id);
    this.requestRender({ board: true });
    this.syncBuyButton();
  }

  // The Size slider: 0.5 to the design's largest size (≤ 1.5) of the fitted size
  setDesignSize(size) {
    this.designSize = size;
    const layer = this.layers.find((item) => item.kind === 'image');
    if (!layer) {
      this.syncSizeSlider(null, size);
      return;
    }
    layer.size = size;
    this.fitChestLayer(layer);
    this.requestRender({ board: true });
  }

  addText() {
    this.setDrawing(false);
    const area = panelInnerRect(this.activePanel);
    const size = Number(this.$('[data-vt-font-size]')?.value) || 42;
    const layer = {
      id: uid(),
      kind: 'text',
      text: 'Your text',
      color: this.$('[data-vt-text-color]')?.value || '#111111',
      font: this.$('[data-vt-font]')?.value || 'Inter',
      x: area.x + area.width / 2,
      y: area.y + area.height / 2,
      w: 0,
      h: size * TEXT_LINE_HEIGHT,
      rotation: 0,
    };
    this.rasterizeText(layer);
    this.layers.push(layer);
    this.select(layer.id);
    this.requestRender({ board: true });
    this.loadFont(layer.font);
    const input = this.$('[data-vt-text-value]');
    input?.focus();
    input?.select();
  }

  // Text is rendered once into its own canvas so it moves, scales and rotates
  // exactly like an image layer. Width follows the text; height is the font size.
  rasterizeText(layer) {
    const font = `600 ${TEXT_RASTER_SIZE}px "${layer.font}", Arial, sans-serif`;
    this.measureCtx.font = font;
    const pad = TEXT_RASTER_SIZE * 0.1;
    const canvas = layer.image || document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(this.measureCtx.measureText(layer.text || ' ').width + pad * 2));
    canvas.height = Math.ceil(TEXT_RASTER_SIZE * TEXT_LINE_HEIGHT);
    const ctx = canvas.getContext('2d');
    ctx.font = font;
    ctx.fillStyle = layer.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(layer.text, canvas.width / 2, canvas.height / 2);
    layer.image = canvas;
    layer.w = (layer.h * canvas.width) / canvas.height;
  }

  updateSelectedText(changes) {
    const layer = this.selectedLayer();
    if (layer?.kind !== 'text') return;
    Object.assign(layer, changes);
    this.rasterizeText(layer);
    this.requestRender({ board: true });
  }

  // Canvas text silently falls back if a web font is not loaded yet, so load
  // it explicitly and redraw any text layers that use it.
  loadFont(font) {
    document.fonts?.load(`600 ${TEXT_RASTER_SIZE}px "${font}"`).then(() => {
      this.layers.filter((layer) => layer.kind === 'text' && layer.font === font)
        .forEach((layer) => this.rasterizeText(layer));
      this.requestRender({ board: true });
    }).catch(() => {});
  }

  syncTextControls() {
    const layer = this.selectedLayer();
    const isText = layer?.kind === 'text';
    const row = this.$('[data-vt-text-row]');
    if (row) row.hidden = !isText;
    if (!isText) return;
    const input = this.$('[data-vt-text-value]');
    if (input && document.activeElement !== input) input.value = layer.text;
    const color = this.$('[data-vt-text-color]');
    if (color) color.value = layer.color;
    const size = this.$('[data-vt-font-size]');
    if (size && document.activeElement !== size) size.value = Math.round(layer.h / TEXT_LINE_HEIGHT);
    const font = this.$('[data-vt-font]');
    if (font) font.value = layer.font;
  }

  deleteSelected() {
    this.layers = this.layers.filter((layer) => layer.id !== this.selectedId);
    this.select(null);
    this.requestRender({ board: true });
  }

  readImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };
      img.src = url;
    });
  }

  saveLayout() {
    // The original pixels and background settings stay in memory only: saving them would double the size
    const payload = this.layers.map(({ image, source, bgAnalysis, bgOptions, ...rest }) => (
      rest.kind === 'image' ? { ...rest, src: image.toDataURL('image/png') } : rest
    ));
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(payload));
      this.flashButton('[data-vt-save]', 'Saved');
    } catch (error) {
      console.warn(error);
      this.flashButton('[data-vt-save]', 'Too large to save');
    }
  }

  async loadLayout() {
    let items = null;
    try {
      items = JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null');
    } catch {
      items = null;
    }
    if (!Array.isArray(items)) return;
    const layers = [];
    for (const item of items) {
      if (item.kind === 'image' && item.src) {
        const { src, ...rest } = item;
        const img = new Image();
        img.src = src;
        await img.decode().catch(() => {});
        layers.push({ ...rest, image: fitImage(img, MAX_LAYER_IMAGE) });
      } else if (item.kind === 'text') {
        const layer = { ...item };
        this.rasterizeText(layer);
        layers.push(layer);
      } else if (item.kind === 'stroke') {
        layers.push(item);
      }
    }
    this.layers = layers;
    this.select(null);
    this.requestRender({ board: true });
    new Set(layers.filter((l) => l.kind === 'text').map((l) => l.font)).forEach((font) => this.loadFont(font));
  }

  flashButton(selector, text) {
    const btn = this.$(selector);
    if (!btn) return;
    clearTimeout(btn.vtFlashTimer);
    btn.dataset.vtLabel ??= btn.textContent;
    btn.textContent = text;
    btn.vtFlashTimer = setTimeout(() => {
      btn.textContent = btn.dataset.vtLabel;
    }, 1600);
  }

  resetDesigns() {
    this.layers = [];
    this.select(null);
    this.requestRender({ board: true });
    if (this.chestPlacement) this.setDesignSize(1);
    this.syncBuyButton();
  }

  // ---------------------------------------------------------------------------
  // Three.js scene
  // ---------------------------------------------------------------------------

  initThree() {
    const stage = this.$('[data-vt-stage]');
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(stage.clientWidth, stage.clientHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    stage.appendChild(this.renderer.domElement);
    this.boardTex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

    this.scene = new THREE.Scene();
    this.setBackground(this.bgHex);

    this.camera = new THREE.PerspectiveCamera(32, stage.clientWidth / stage.clientHeight, 0.05, 50);
    this.camera.position.set(0, 0.2, 2.4);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.enablePan = false;
    this.controls.minDistance = 1.1;
    this.controls.maxDistance = 4.5;
    this.controls.autoRotateSpeed = 4;

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x222233, 1.1));
    const key = new THREE.DirectionalLight(0xffffff, 2.1);
    key.position.set(2.4, 3.2, 4);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xc9d6ff, 0.7);
    fill.position.set(-3, 1, -2);
    this.scene.add(fill);

    window.addEventListener('resize', () => this.resize());
    this.loop();
  }

  // A colour replaces any background image.
  setBackground(hex) {
    this.bgHex = hex;
    this.bgTexture?.dispose();
    this.bgTexture = null;
    this.scene.background = hexToColor(hex, '#111111');
    this.syncBackgroundControls();
  }

  async onBackgroundUpload(file) {
    if (!file) return;
    try {
      const image = fitImage(await this.readImage(file), 2048);
      this.bgTexture?.dispose();
      this.bgTexture = new THREE.CanvasTexture(image);
      this.bgTexture.colorSpace = THREE.SRGBColorSpace;
      this.scene.background = this.bgTexture;
      this.fitBackground(this.camera.aspect);
      this.syncBackgroundControls();
    } catch (error) {
      console.error(error);
    }
    this.$('[data-vt-bg-file]').value = '';
  }

  // "Cover" the viewport: scale the image to fill it and crop the overflow
  // evenly, so it never stretches whatever the viewer or export aspect is.
  fitBackground(aspect) {
    const texture = this.bgTexture;
    if (!texture) return;
    const imageAspect = texture.image.width / texture.image.height;
    if (aspect > imageAspect) {
      texture.repeat.set(1, imageAspect / aspect);
    } else {
      texture.repeat.set(aspect / imageAspect, 1);
    }
    texture.offset.set((1 - texture.repeat.x) / 2, (1 - texture.repeat.y) / 2);
  }

  syncBackgroundControls() {
    const clear = this.$('[data-vt-bg-clear]');
    if (clear) clear.hidden = !this.bgTexture;
  }

  // ---------------------------------------------------------------------------
  // Product types (assets/custom-product-types.json)
  // ---------------------------------------------------------------------------

  // Loads the product types, then the first model: the ?type= of the link, else the section's
  // default type, else the first one. Without types, the section's model with its print area.
  async initProductTypes() {
    let types = [];
    let currency = null;
    if (this.typesUrl) {
      try {
        const response = await fetch(this.typesUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const config = await response.json();
        currency = config.currency || null;
        this.mockupOrigin = String(config.mockupOrigin || '').replace(/\/$/, '') || null;
        types = (config.types || []).filter((type) => type && type.id && type.model);
      } catch (error) {
        console.warn('3D configurator: product types not loaded, using the section model', error);
      }
    }
    this.types = types;
    this.currency = currency;
    if (!types.length) {
      this.loadModel(this.modelUrl);
      return;
    }
    const select = this.$('[data-vt-type]');
    if (select) {
      select.replaceChildren(...types.map((type) => new Option(type.name || type.id, type.id)));
    }
    this.root.classList.add('has-product-types');
    const product = this.$('[data-vt-product]');
    const tab = this.$('[data-vt-product-tab]');
    if (tab) tab.hidden = false;
    // Phones: the product sheet stays closed until its tab is used
    if (product) product.hidden = !!window.matchMedia?.('(max-width: 860px)').matches;
    this.syncMobileSheet();

    const wanted = new URLSearchParams(window.location.search).get('type') || this.root.dataset.defaultType;
    this.selectType(types.some((type) => type.id === wanted) ? wanted : types[0].id);
  }

  selectType(id) {
    const type = this.types.find((item) => item.id === id);
    if (!type || type === this.type) return;
    this.type = type;
    this.placementMode = type.placement === 'wrap' ? 'wrap' : 'chest';
    const area = type.printArea || {};
    const fraction = (value, fallback) => (value !== undefined && value !== null && Number.isFinite(Number(value)) ? Number(value) : fallback) / 100;
    const defaults = this.placementMode === 'wrap' ? { top: 12, width: 42, height: 76 } : { top: 13, width: 49, height: 49 };
    this.printArea = {
      top: fraction(area.top, defaults.top),
      width: fraction(area.width, defaults.width),
      height: fraction(area.height, defaults.height),
      // Garments: the highest point an enlarged design reaches (the type's, else the section's);
      // a cup has no neckline
      limitTop: this.placementMode === 'wrap' ? 0 : fraction(area.limitTop, this.sectionLimitTop * 100),
    };
    // The Size slider's largest value (a tumbler: only its print area, like the Mockup app)
    this.maxDesignSize = fraction(type.maxSize, 150);
    const select = this.$('[data-vt-type]');
    if (select && select.value !== id) select.value = id;
    this.applyColors(type);
    // The size the customer picked last that this type has (M stays M), else its first size
    const sizes = (type.sizes || []).map(String);
    this.renderSizes(sizes);
    this.selectSize(this.sizeHistory.find((size) => sizes.includes(size)) ?? sizes[0] ?? null);
    this.loadModel(type.model);
  }

  // The garment colors of a type ("colors" in custom-product-types.json): absent = any color (the
  // picker); a list = only those swatches; false = the product's own color (a mug), no choice
  applyColors(type) {
    const valid = (color) => /^#[0-9a-f]{6}$/i.test(color?.hex || '');
    this.fixedColor = type.colors === false;
    this.colorSwatches = Array.isArray(type.colors) ? type.colors.filter(valid) : null;
    if (this.colorSwatches && !this.colorSwatches.length) this.colorSwatches = null;
    this.$all('[data-vt-toggle="color"]').forEach((button) => { button.hidden = this.fixedColor; });
    const panel = this.$('[data-vt-panel="color"]');
    if (panel && this.fixedColor) panel.hidden = true;
    const picker = this.$('[data-vt-color-picker]');
    if (picker) picker.hidden = !!this.colorSwatches;
    const list = this.$('[data-vt-color-swatches]');
    if (list) {
      list.hidden = !this.colorSwatches;
      list.replaceChildren(...(this.colorSwatches || []).map((color) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vt-swatch';
        button.dataset.vtSwatch = color.hex.toLowerCase();
        const dot = document.createElement('span');
        dot.className = 'vt-swatch__dot';
        dot.style.background = color.hex;
        button.append(dot, color.name || color.hex);
        return button;
      }));
    }
    if (this.fixedColor) {
      this.setGarmentColor('#ffffff');
    } else if (this.colorSwatches) {
      const same = this.colorSwatches.find((color) => color.hex.toLowerCase() === this.garmentHex);
      this.setGarmentColor((same || this.colorSwatches[0]).hex);
    } else {
      this.setGarmentColor(this.$('[data-vt-garment-color]')?.value || '#ffffff');
    }
  }

  // The chosen garment color (sent with Buy now); a swatch may show another shade on the model
  setGarmentColor(hex) {
    this.garmentHex = hex.toLowerCase();
    this.garmentPreview = this.colorSwatches?.find((color) => color.hex.toLowerCase() === this.garmentHex)?.preview || null;
    this.$all('[data-vt-swatch]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.vtSwatch === this.garmentHex));
    });
    this.syncTint();
  }

  // The color the model is tinted with
  tintHex() {
    return this.garmentPreview || this.garmentHex;
  }

  // The size guide link and dialog follow the product type and the selected size
  syncSizeGuide() {
    const id = this.type?.id || '';
    let current = null;
    this.$all('[data-vt-size-chart]').forEach((chart) => {
      const on = chart.dataset.vtSizeChart === id;
      chart.hidden = !on;
      if (on) current = chart;
    });
    const rows = current ? [...current.querySelectorAll('tr[data-size]')] : [];
    rows.forEach((row) => row.classList.toggle('is-current', rows.length > 1 && row.dataset.size === this.size));
    const button = this.$('[data-vt-size-guide]');
    if (button) {
      button.hidden = !current;
      // One row (a cup): its dimensions rather than a size guide
      button.textContent = rows.length === 1 ? 'Dimensions' : 'Size guide';
    }
  }

  setChartUnit(unit, remember = true) {
    const dialog = this.$('[data-vt-size-dialog]');
    if (!dialog) return;
    dialog.dataset.unit = unit;
    dialog.querySelectorAll('[data-unit-button]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.unitButton === unit));
    });
    if (!remember) return;
    try {
      window.localStorage.setItem('size-chart-unit', unit);
    } catch {
      // Storage unavailable: the unit simply isn't remembered
    }
  }

  renderSizes(sizes) {
    const list = this.$('[data-vt-sizes]');
    if (!list) return;
    list.replaceChildren(...sizes.map((size) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vt-size';
      button.setAttribute('role', 'radio');
      button.dataset.vtSize = size;
      button.textContent = size;
      return button;
    }));
    // One size (a cup): nothing to choose
    const field = list.closest('.vt-product__field');
    if (field) field.hidden = sizes.length < 2;
  }

  selectSize(size) {
    this.size = size;
    this.$all('[data-vt-size]').forEach((button) => {
      const on = button.dataset.vtSize === size;
      button.classList.toggle('is-active', on);
      button.setAttribute('aria-checked', String(on));
    });
    this.syncSizeGuide();
    this.syncPrice();
  }

  setQuantity(value) {
    this.quantity = Math.min(99, Math.max(1, Math.round(Number.isFinite(value) ? value : 1)));
    const input = this.$('[data-vt-qty]');
    if (input) input.value = String(this.quantity);
    this.syncPrice();
  }

  // The price shown only: Buy now looks the order's price up again on the server
  unitPrice() {
    const type = this.type;
    if (!type) return null;
    const price = Number(type.sizePrices?.[this.size] ?? type.price);
    return Number.isFinite(price) ? price : null;
  }

  syncPrice() {
    const el = this.$('[data-vt-price]');
    const unit = this.unitPrice();
    if (el) {
      if (unit === null) {
        el.textContent = '';
      } else {
        const format = (amount) => {
          try {
            return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: this.currency || 'USD' }).format(amount);
          } catch {
            return amount.toFixed(2);
          }
        };
        el.textContent = this.quantity > 1 ? `${format(unit * this.quantity)} (${this.quantity} × ${format(unit)})` : format(unit);
      }
    }
    this.root.dispatchEvent(new CustomEvent('vt:selection', { bubbles: true, detail: this.selection() }));
    this.syncBuyButton();
  }

  // What the customer picked, for Buy now
  selection() {
    return { type: this.type?.id ?? null, size: this.size, quantity: this.quantity };
  }

  // Frees the previous model's GPU memory before another one is shown
  disposeModel() {
    if (!this.model) return;
    this.scene.remove(this.model);
    this.model.traverse((obj) => {
      if (!obj.isMesh) return;
      obj.geometry?.dispose();
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      materials.forEach((material) => {
        Object.values(material || {}).forEach((value) => value?.isTexture && value.dispose());
        material?.dispose();
      });
    });
    this.sourceGeometry?.dispose();
    this.model = null;
    this.shirt = null;
    this.sourceGeometry = null;
    this.boardTriangles = null;
    this.frontPieceRect = null;
    this.silhouettePaths = null;
  }

  
  // A model file's bytes. Shopify's CDN lets the browser cache models for a year, so a file
  // downloaded once (here or by preloadModels) comes from the browser's disk cache afterwards; only
  // a download in progress is shared here, nothing is kept in memory (uncompressed models are
  // 60 MB each).
  modelFile(url) {
    this.modelDownloads ??= new Map();
    if (!this.modelDownloads.has(url)) {
      const request = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
        return response.arrayBuffer();
      });
      this.modelDownloads.set(url, request);
      this.modelsFetched ??= new Set();
      request.then(() => this.modelsFetched.add(url), () => {}).finally(() => this.modelDownloads.delete(url));
    }
    return this.modelDownloads.get(url);
  }

  // Downloads the other product types' models into the browser's cache, one after another in the
  // list's order, so picking another type shows it without waiting for the network. Skipped when
  // the visitor asked to save data or is on a very slow connection.
  async preloadModels() {
    if (this.preloading || this.types.length < 2) return;
    this.preloading = true;
    const connection = navigator.connection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    for (const type of this.types) {
      if (this.modelsFetched?.has(type.model)) continue;
      // Only compressed models: an uncompressed one (60 MB) is downloaded when it is picked
      const head = await fetch(type.model, { method: 'HEAD' }).catch(() => null);
      const bytes = Number(head?.headers.get('content-length'));
      if (!head?.ok || !(bytes > 0) || bytes > MAX_PRELOAD_BYTES) continue;
      // The bytes are dropped right away: the browser's cache keeps the file
      await this.modelFile(type.model).catch(() => null);
    }
  }

  async loadModel(url = this.modelUrl) {
    const loader = new GLTFLoader();
    // Models compressed with Draco (geometry) and WebP (textures) are about a tenth of the size;
    // uncompressed models load as before. One decoder is shared by every model.
    if (!this.dracoLoader) {
      this.dracoLoader = new DRACOLoader();
      this.dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
    }
    loader.setDRACOLoader(this.dracoLoader);
    // Only the latest request is shown when the type changes during a download
    const token = (this.modelToken = (this.modelToken || 0) + 1);
    const loaderEl = this.$('[data-vt-loader]');
    if (loaderEl) {
      loaderEl.hidden = false;
      loaderEl.querySelector('p').textContent = 'Loading 3D mockup…';
    }
    try {
      // The file may already be downloaded in the background (preloadModels)
      const gltf = await loader.parseAsync(await this.modelFile(url), '');
      if (token !== this.modelToken) return;
      this.disposeModel();
      // A group around the file's scene: loop() resets the group's rotation every frame, while a
      // cup's turn (handle to the right) stays on the scene inside it
      this.model = new THREE.Group();
      this.model.add(gltf.scene);
      this.model.traverse((obj) => {
        if (obj.isMesh) {
          obj.castShadow = true;
          this.shirt = obj;
          const mat = obj.material;
          if (Array.isArray(mat)) obj.material = mat[0];
          this.patchMaterial(obj.material);
        }
      });
      if (this.placementMode === 'wrap') this.turnHandle(gltf.scene, this.type?.handle === 'left' ? 'left' : 'right');
      const box = new THREE.Box3().setFromObject(this.model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 1.7 / maxDim;

      // Scale model
      this.model.scale.setScalar(scale);

      // Đưa TÂM HÌNH HỌC của áo về đúng (0, 0, 0)
      this.model.position.set(
        -center.x * scale,
        -center.y * scale,
        -center.z * scale
      );

      // Lưu vị trí chuẩn để animation không phá nó
      this.model.userData.baseScale = scale;
      this.model.userData.basePosition = this.model.position.clone();
      this.scene.add(this.model);
      this.modelSize = size.clone().multiplyScalar(scale);
      this.controls.target.set(0, 0, 0);
      this.camera.position.set(0, 0, 1);
      this.fitDistanceValue = this.fitDistance(this.camera.aspect);
      this.setCameraDistance(this.fitDistanceValue);
      if (this.camZoom) this.zoomBaseDistance = this.fitDistanceValue;
      this.updateZoomLimits();
      this.buildBoardProjection();
      // Object-space height range drives the knit reveal and the wave falloff.
      const geometry = this.shirt.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      this.shirt.material.userData.vt?.uHeight.value.set(geometry.boundingBox.min.y, geometry.boundingBox.max.y);
      this.$('[data-vt-loader]').hidden = true;
      // The first model is on screen: download the other types' models while the customer designs
      this.preloadModels();
    } catch (err) {
      if (token !== this.modelToken) return;
      const loaderEl = this.$('[data-vt-loader]');
      loaderEl.querySelector('p').textContent = 'Could not load 3D model. Check the GLB asset filename.';
      console.error(err);
    }
  }

  // ---------------------------------------------------------------------------
  // Buy now: the product image (2D mockup, like the Mockup app), the customer's product, checkout
  // ---------------------------------------------------------------------------

  // The uploaded design (chest placement: there is only one)
  designLayer() {
    return this.layers.find((layer) => layer.kind === 'image') || null;
  }

  // Buy now needs product types, the chest placement, a design, a size and a name
  syncBuyButton() {
    const button = this.$('[data-vt-buy]');
    if (!button) return;
    const name = this.$('[data-vt-design-name]')?.value.trim();
    const ready = this.chestPlacement && !!this.type && !!this.size && !!this.designLayer() && !!name;
    button.disabled = this.buying || !ready;
    button.hidden = !this.chestPlacement || !this.types.length;
    if (!this.buying) {
      const hint = !this.designLayer() ? 'Upload your design to buy it.' : !name ? 'Name your design to buy it.' : '';
      this.setBuyStatus(hint);
    }
  }

  setBuyStatus(text, isError = false) {
    const status = this.$('[data-vt-buy-status]');
    if (!status) return;
    status.textContent = text;
    status.classList.toggle('is-error', isError);
  }

  loadMockupManifest() {
    this.mockupManifest ??= fetch(`${this.mockupOrigin}/templates/manifest.json`).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }).catch((error) => {
      this.mockupManifest = null;
      // The app's templates unreachable (not deployed, or no CORS header on /templates/*)
      console.error('3D configurator: mockup templates not loaded from', this.mockupOrigin, error);
      throw new Error('Buy now is not available right now (the product image service did not answer). Please try again later.');
    });
    return this.mockupManifest;
  }

  loadMockupImage(path) {
    this.mockupImages ??= new Map();
    const src = /^https?:/.test(path) ? path : `${this.mockupOrigin}${path}`;
    if (!this.mockupImages.has(src)) {
      this.mockupImages.set(src, new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => {
          this.mockupImages.delete(src);
          reject(new Error(`Mockup image not loaded: ${src}`));
        };
        img.src = src;
      }));
    }
    return this.mockupImages.get(src);
  }

  /**
   * The product image: the Mockup app's template for this type, drawn as the app draws its mockups
   * (src/lib/mockup.ts renderMockup): the photo (a fabric color: the white blank tinted, black: the
   * black photo), then the design fitted into every print area at the Size slider's size, with the
   * fabric's shading soft-lit onto it.
   */
  async renderMockup2d() {
    const layer = this.designLayer();
    if (!layer || !this.type?.mockupTemplate || !this.mockupOrigin) throw new Error('No mockup for this product');
    const manifest = await this.loadMockupManifest();
    let template = manifest[this.type.mockupTemplate];
    if (!template) throw new Error(`Unknown mockup template ${this.type.mockupTemplate}`);
    const color = (this.garmentHex || '').toLowerCase();
    const tint = template.colorBase && /^#[0-9a-f]{6}$/.test(color) && color !== '#000000' ? color : null;
    if (tint) template = { ...template, ...template.colorBase };
    const [photo, shading] = await Promise.all([
      this.loadMockupImage(template.image),
      template.shading ? this.loadMockupImage(template.shading).catch(() => null) : null,
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = template.width;
    canvas.height = template.height;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    if (tint) {
      // The white blank's brightness multiplied by the color, on the white background of the photos
      const tinted = document.createElement('canvas');
      tinted.width = photo.naturalWidth;
      tinted.height = photo.naturalHeight;
      const t = tinted.getContext('2d');
      t.drawImage(photo, 0, 0);
      t.globalCompositeOperation = 'multiply';
      t.fillStyle = tint;
      t.fillRect(0, 0, tinted.width, tinted.height);
      t.globalCompositeOperation = 'destination-in';
      t.drawImage(photo, 0, 0);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(tinted, 0, 0, template.width, template.height);
    } else {
      ctx.drawImage(photo, 0, 0, template.width, template.height);
    }

    const design = layer.image;
    const dw = design.naturalWidth || design.width;
    const dh = design.naturalHeight || design.height;
    const scale = layer.size || 1;
    (template.printAreas || []).forEach((area) => {
      // Mockup app designRect: fitted (contain) into the area, scaled, centred
      const fit = Math.min(area.width / dw, area.height / dh) * scale;
      const w = dw * fit;
      const h = dh * fit;
      const rect = { x: area.x + (area.width - w) / 2, y: area.y + (area.height - h) / 2, width: w, height: h };
      const ink = document.createElement('canvas');
      ink.width = Math.max(1, Math.round(area.width));
      ink.height = Math.max(1, Math.round(area.height));
      const l = ink.getContext('2d');
      l.scale(ink.width / area.width, ink.height / area.height);
      const drawInk = () => l.drawImage(design, rect.x - area.x, rect.y - area.y, rect.width, rect.height);
      drawInk();
      if (shading) {
        // Folds darken the print and highlights brighten it, only where the design has ink
        l.globalCompositeOperation = 'soft-light';
        l.drawImage(shading, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
        l.globalCompositeOperation = 'destination-in';
        drawInk();
      }
      ctx.drawImage(ink, area.x, area.y, area.width, area.height);
    });
    return canvas;
  }

  // Turnstile (optional captcha): its script once, its widget in the product block
  async captchaToken() {
    if (!this.turnstileKey) return null;
    if (!window.turnstile) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.onload = resolve;
        script.onerror = () => reject(new Error('The verification could not load'));
        document.head.appendChild(script);
      });
    }
    const box = this.$('[data-vt-captcha]');
    if (this.captchaWidget === undefined && box) {
      this.captchaWidget = window.turnstile.render(box, { sitekey: this.turnstileKey });
    }
    const token = window.turnstile.getResponse(this.captchaWidget);
    if (!token) throw new Error('Please complete the verification, then press Buy now again.');
    return token;
  }

  async postBuy(step, body) {
    const response = await fetch(`${this.buyUrl}/${step}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    }).catch((error) => {
      console.error('3D configurator: Buy now backend unreachable', this.buyUrl, error);
      throw new Error('Buy now is not available right now (the store did not answer). Please try again later.');
    });
    const data = await response.json().catch(() => null);
    // A 404 without JSON: the App Proxy (or the backend behind it) isn't set up
    if (!response.ok || !data) throw new Error(data?.error || (response.status === 404 ? 'Buy now is not set up on this store yet.' : `The store could not create your product (HTTP ${response.status})`));
    return data;
  }

  // The same choices keep their request key: pressing Buy now again (or a retry after an error) gets
  // the product already made instead of a second one
  buyKey(name) {
    const layer = this.designLayer();
    const choice = JSON.stringify([this.type?.id, this.size, this.quantity, name, this.garmentHex, layer?.id, layer?.size, layer?.bgOptions]);
    if (this.buyChoice !== choice) {
      this.buyChoice = choice;
      this.buyRequestKey = (crypto.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^a-z0-9]/gi, '').slice(0, 32);
    }
    return this.buyRequestKey;
  }

  async waitForProduct(handle) {
    for (let i = 0; i < 20; i += 1) {
      const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(handle)}.js`, { headers: { accept: 'application/json' } }).catch(() => null);
      if (response?.ok) return;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  // Creates the customer's product (the Mockup app's backend) and opens its checkout
  async buyNow() {
    const name = this.$('[data-vt-design-name]')?.value.replace(/\s+/g, ' ').trim();
    const layer = this.designLayer();
    if (this.buying || !layer || !this.type || !this.size || !name) return;
    this.buying = true;
    this.syncBuyButton();
    try {
      const captcha = await this.captchaToken();
      this.setBuyStatus('Preparing your product…');
      const toBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The image could not be saved'))), type, quality);
      });
      // The design at full size, made smaller only when its PNG is over the upload limit
      const designBlob = async () => {
        let image = layer.image;
        for (;;) {
          const blob = await toBlob(image, 'image/png');
          const side = Math.max(image.width, image.height);
          if (blob.size <= MAX_DESIGN_BYTES || side <= 1024) return blob;
          image = fitImage(image, Math.round(side * 0.8));
        }
      };
      const [mockup, design] = await Promise.all([
        this.renderMockup2d().then((canvas) => toBlob(canvas, 'image/jpeg', 0.92)),
        designBlob(),
      ]);
      const files = { mockup, design };
      const prepared = await this.postBuy('prepare', {
        type: this.type.id,
        size: this.size,
        quantity: this.quantity,
        name,
        key: this.buyKey(name),
        // A product in its own color (a mug) has no color to choose
        color: this.fixedColor ? null : this.garmentHex,
        designSize: layer.size || 1,
        captcha,
        files: Object.entries(files).map(([role, blob]) => ({ role, mimeType: blob.type, size: blob.size })),
      });

      this.setBuyStatus('Uploading your design…');
      await Promise.all(prepared.uploads.map(async (target) => {
        const form = new FormData();
        target.parameters.forEach((p) => form.append(p.name, p.value));
        form.append('file', files[target.role], `${target.role}.${files[target.role].type === 'image/png' ? 'png' : 'jpg'}`);
        const response = await fetch(target.url, { method: 'POST', body: form });
        if (!response.ok) throw new Error(`Upload failed (HTTP ${response.status})`);
      }));

      this.setBuyStatus('Creating your product…');
      const created = await this.postBuy('create', { ticket: prepared.ticket });
      await this.waitForProduct(created.handle);
      this.setBuyStatus('Opening checkout…');
      // A cart permalink: a checkout with only this product
      window.location.assign(`${this.rootUrl}cart/${created.variantId}:${created.quantity}`);
    } catch (error) {
      console.error(error);
      this.buying = false;
      this.syncBuyButton();
      this.setBuyStatus(error.message || 'Something went wrong, please try again.', true);
      if (this.captchaWidget !== undefined) window.turnstile?.reset(this.captchaWidget);
    }
  }

  // ---------------------------------------------------------------------------
  // Cups and tumblers ('wrap')
  // ---------------------------------------------------------------------------

  // The cup's vertical axis, its outer radius at each height and its handle, from world positions,
  // by shape rather than by vertex counts (a generated handle can hold a quarter of the vertices):
  // the side's farthest extent in 72 directions, with a circle fitted to the 60% of directions
  // closest to it (the handle only ever pushes the outline outwards). Y is up.
  cupProfile(world, count) {
    const DIRS = 72;
    const cos = Float64Array.from({ length: DIRS }, (_, k) => Math.cos((2 * Math.PI * k) / DIRS));
    const sin = Float64Array.from({ length: DIRS }, (_, k) => Math.sin((2 * Math.PI * k) / DIRS));
    const support = (ids) => {
      const h = new Float64Array(DIRS).fill(-Infinity);
      ids.forEach((i) => {
        const x = world[i * 3];
        const z = world[i * 3 + 2];
        for (let k = 0; k < DIRS; k += 1) h[k] = Math.max(h[k], x * cos[k] + z * sin[k]);
      });
      return h;
    };
    // h_k = cx cos_k + cz sin_k + R, least squares over the kept directions
    const det3 = (m) => m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1])
      - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0])
      + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
    const fitCircle = (h) => {
      let keep = Array.from({ length: DIRS }, (_, k) => k);
      let fit = { cx: 0, cz: 0, R: 1 };
      for (let pass = 0; pass < 10; pass += 1) {
        const A = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
        const b = [0, 0, 0];
        keep.forEach((k) => {
          const row = [cos[k], sin[k], 1];
          for (let r = 0; r < 3; r += 1) {
            b[r] += row[r] * h[k];
            for (let c = 0; c < 3; c += 1) A[r][c] += row[r] * row[c];
          }
        });
        const D = det3(A);
        if (Math.abs(D) < 1e-12) break;
        const [cx, cz, R] = [0, 1, 2].map((c) => det3(A.map((row, r) => row.map((v, j) => (j === c ? b[r] : v)))) / D);
        fit = { cx, cz, R };
        const residual = (k) => h[k] - (cx * cos[k] + cz * sin[k] + R);
        const next = Array.from({ length: DIRS }, (_, k) => k)
          .sort((a, c) => residual(a) - residual(c))
          .slice(0, Math.round(DIRS * 0.6))
          .sort((a, c) => a - c);
        if (next.join() === keep.join()) break;
        keep = next;
      }
      return fit;
    };

    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < count; i += 1) {
      minY = Math.min(minY, world[i * 3 + 1]);
      maxY = Math.max(maxY, world[i * 3 + 1]);
    }
    const height = maxY - minY || 1;
    const all = Array.from({ length: count }, (_, i) => i);
    // The axis from the middle of the height, away from a lid, a rim or a foot
    const middle = all.filter((i) => world[i * 3 + 1] > minY + height * 0.3 && world[i * 3 + 1] < minY + height * 0.7);
    const axis = fitCircle(support(middle.length > 50 ? middle : all));
    const { cx, cz } = axis;
    // Outer radius per height (tumblers taper): the median over directions of the slice's extent
    const SLICES = 16;
    const sliceOf = (y) => Math.min(SLICES - 1, Math.floor(((y - minY) / height) * SLICES));
    const groups = Array.from({ length: SLICES }, () => []);
    all.forEach((i) => groups[sliceOf(world[i * 3 + 1])].push(i));
    const outer = groups.map((ids) => {
      if (!ids.length) return 0;
      const h = support(ids);
      const extents = Array.from(h, (value, k) => value - (cx * cos[k] + cz * sin[k])).sort((a, c) => a - c);
      return extents[Math.floor(DIRS / 2)];
    });
    // The handle: beyond the outer radius of its height, all on one side
    const handle = new Uint8Array(count);
    let hx = 0;
    let hz = 0;
    let hr = 0;
    let handleCount = 0;
    for (let i = 0; i < count; i += 1) {
      const dx = world[i * 3] - cx;
      const dz = world[i * 3 + 2] - cz;
      const r = Math.hypot(dx, dz);
      if (r <= outer[sliceOf(world[i * 3 + 1])] * 1.08) continue;
      handle[i] = 1;
      hx += dx;
      hz += dz;
      hr += r;
      handleCount += 1;
    }
    // Their mean direction is long for a handle, short for a flared lid all the way round
    const concentration = handleCount ? Math.hypot(hx, hz) / hr : 0;
    return {
      cx,
      cz,
      minY,
      maxY,
      radius: axis.R,
      handle,
      handleAngle: handleCount > count * 0.005 && concentration > 0.5 ? Math.atan2(hx, hz) : null,
    };
  }

  // Turns a cup so its handle points right (+X) or left (-X): the side facing the viewer is then
  // free for the design, with the handle still in view. A mug (handle left) is printed on the side
  // right of its handle, as in the Mockup app's photos.
  turnHandle(scene, side) {
    scene.updateMatrixWorld(true);
    let mesh = null;
    scene.traverse((obj) => {
      if (obj.isMesh) mesh = obj;
    });
    const position = mesh?.geometry?.attributes?.position;
    if (!position) return;
    const count = position.count;
    const world = new Float32Array(count * 3);
    const v = new THREE.Vector3();
    for (let i = 0; i < count; i += 1) {
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      world[i * 3] = v.x;
      world[i * 3 + 1] = v.y;
      world[i * 3 + 2] = v.z;
    }
    const { handleAngle } = this.cupProfile(world, count);
    if (handleAngle === null) return;
    // rotateY(a) adds a to atan2(x, z); +X is atan2 = PI / 2, -X is -PI / 2
    scene.rotation.y += (side === 'left' ? -Math.PI / 2 : Math.PI / 2) - handleAngle;
    scene.updateMatrixWorld(true);
  }

  // The cup's side unrolled into one board panel: across = the angle around the axis (the side
  // facing the viewer, +Z, in the middle; the seam at the back), down = the height. The handle,
  // the inside, the rim and the bottom (not facing outwards) get no design.
  buildWrapProjection({ mesh, geometry, world, count, index, total, vertexAt, uv }) {
    const profile = this.cupProfile(world, count);
    const { cx, cz, minY, maxY } = profile;
    const height = maxY - minY || 1;
    // Same scale across and down, so the design keeps its proportions on the side
    let width = BOARD_W;
    let boardHeight = (BOARD_W * height) / (2 * Math.PI * profile.radius);
    if (boardHeight > BOARD_H) {
      width *= BOARD_H / boardHeight;
      boardHeight = BOARD_H;
    }
    const around = (id) => (Math.atan2(world[id * 3] - cx, world[id * 3 + 2] - cz) + Math.PI) / (2 * Math.PI);

    const corners = new Float32Array(total * 2).fill(-1);
    const cornerIds = new Uint32Array(total);
    const regions = new Array(Math.floor(total / 3));
    const placed = new Uint8Array(Math.floor(total / 3));
    for (let offset = 0; offset + 2 < total; offset += 3) {
      const ids = [vertexAt(offset), vertexAt(offset + 1), vertexAt(offset + 2)];
      ids.forEach((id, k) => { cornerIds[offset + k] = id; });
      if (ids.some((id) => profile.handle[id])) continue;
      const [a, b, c] = ids;
      const ux = world[b * 3] - world[a * 3], uy = world[b * 3 + 1] - world[a * 3 + 1], uz = world[b * 3 + 2] - world[a * 3 + 2];
      const wx = world[c * 3] - world[a * 3], wy = world[c * 3 + 1] - world[a * 3 + 1], wz = world[c * 3 + 2] - world[a * 3 + 2];
      const nx = uy * wz - uz * wy;
      const ny = uz * wx - ux * wz;
      const nz = ux * wy - uy * wx;
      const length = Math.hypot(nx, ny, nz) || 1;
      const mx = (world[a * 3] + world[b * 3] + world[c * 3]) / 3 - cx;
      const mz = (world[a * 3 + 2] + world[b * 3 + 2] + world[c * 3 + 2]) / 3 - cz;
      const radial = Math.hypot(mx, mz) || 1;
      // Facing outwards, not up or down: the outer side
      if ((nx * mx + nz * mz) / (length * radial) < 0.25) continue;
      // Continuous across the seam within a triangle
      const u0 = around(a);
      const us = ids.map((id) => u0 + ((((around(id) - u0) % 1) + 1.5) % 1) - 0.5);
      ids.forEach((id, k) => {
        corners[(offset + k) * 2] = us[k] * width;
        corners[(offset + k) * 2 + 1] = ((maxY - world[id * 3 + 1]) / height) * boardHeight;
      });
      regions[offset / 3] = 'wrap';
      placed[offset / 3] = 1;
    }

    // The handle (turned to +X: around = 0.75, -X: 0.25) limits how wide the design grows from the middle
    const handleU = profile.handleAngle === null ? null : (profile.handleAngle + Math.PI) / (2 * Math.PI);
    const room = handleU === null ? 0.8 : 2 * Math.max(0.1, Math.abs(handleU - 0.5) - 0.07);
    this.frontPieceRect = { x: 0, y: 0, width, height: boardHeight, centerX: width / 2, wrapRoom: room * width };

    const boardUv = new Float32Array(total * 2);
    for (let o = 0; o < total; o += 1) {
      const outside = corners[o * 2] < 0 && corners[o * 2 + 1] < 0;
      boardUv[o * 2] = outside ? -1 : corners[o * 2] / BOARD_W;
      boardUv[o * 2 + 1] = outside ? -1 : corners[o * 2 + 1] / BOARD_H;
    }
    this.collarMarks = [];
    this.sourceGeometry = geometry;
    const flat = index ? geometry.toNonIndexed() : geometry.clone();
    flat.setAttribute('vtBoard', new THREE.BufferAttribute(boardUv, 2));
    mesh.geometry = flat;
    this.boardTriangles = { corners, cornerIds, uv, placed, regions, collarRows: null };
    this.silhouettePaths = null;
    this.silhouetteDirty = true;
    if (this.chestPlacement) {
      this.layers.filter((layer) => layer.kind === 'image').forEach((layer) => this.fitChestLayer(layer));
    }
    this.requestRender({ board: true });
  }

  buildProjectionCameras() {
    const make = (pos, target = new THREE.Vector3(0, 0, 0)) => {
      const cam = new THREE.OrthographicCamera();
      cam.position.copy(pos);
      cam.lookAt(target);
      cam.updateMatrixWorld(true);
      return cam;
    };
    return {
      front: make(new THREE.Vector3(0, 0.05, 2.2)),
      back: make(new THREE.Vector3(0, 0.05, -2.2)),
      left: make(new THREE.Vector3(-2.2, 0.62, 1.65), new THREE.Vector3(-0.52, 0.38, 0)),
      right: make(new THREE.Vector3(2.2, 0.62, 1.65), new THREE.Vector3(0.52, 0.38, 0)),
    };
  }

  // Splits the mesh into its garment pieces, projects each piece into its board
  // panel and stores the resulting board position on every vertex (`vtBoard`).
  // The shader then samples the board texture at that position, so the GPU does
  // the board -> garment mapping and moving a layer only redraws a 2D canvas.
  buildBoardProjection() {
    const mesh = this.shirt;
    const geometry = mesh?.geometry;
    const position = geometry?.attributes?.position;
    const uv = geometry?.attributes?.uv;
    if (!position || !uv || !this.model) return;
    this.model.updateMatrixWorld(true);

    const count = position.count;
    const world = new Float32Array(count * 3);
    const v = new THREE.Vector3();
    const bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, minZ: Infinity, maxZ: -Infinity };
    for (let i = 0; i < count; i += 1) {
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      world[i * 3] = v.x;
      world[i * 3 + 1] = v.y;
      world[i * 3 + 2] = v.z;
      bounds.minX = Math.min(bounds.minX, v.x);
      bounds.maxX = Math.max(bounds.maxX, v.x);
      bounds.minY = Math.min(bounds.minY, v.y);
      bounds.maxY = Math.max(bounds.maxY, v.y);
      bounds.minZ = Math.min(bounds.minZ, v.z);
      bounds.maxZ = Math.max(bounds.maxZ, v.z);
    }
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2;

    const index = geometry.index;
    const vertexAt = (offset) => (index ? index.getX(offset) : offset);
    const total = index ? index.count : count;
    if (this.placementMode === 'wrap') {
      this.buildWrapProjection({ mesh, geometry, world, count, index, total, vertexAt, uv });
      return;
    }

    // Union-find over triangles: each connected component is one garment piece.
    const parents = new Uint32Array(count);
    for (let i = 0; i < count; i += 1) parents[i] = i;
    const findRoot = (id) => {
      let root = id;
      while (parents[root] !== root) root = parents[root];
      while (parents[id] !== root) {
        const next = parents[id];
        parents[id] = root;
        id = next;
      }
      return root;
    };
    const join = (a, b) => {
      const rootA = findRoot(a);
      const rootB = findRoot(b);
      if (rootA !== rootB) parents[rootB] = rootA;
    };
    for (let offset = 0; offset + 2 < total; offset += 3) {
      join(vertexAt(offset), vertexAt(offset + 1));
      join(vertexAt(offset + 1), vertexAt(offset + 2));
    }

    const components = new Map();
    for (let id = 0; id < count; id += 1) {
      const root = findRoot(id);
      let component = components.get(root);
      if (!component) {
        component = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, minZ: Infinity, maxZ: -Infinity, ids: [] };
        components.set(root, component);
      }
      component.minX = Math.min(component.minX, world[id * 3]);
      component.maxX = Math.max(component.maxX, world[id * 3]);
      component.minY = Math.min(component.minY, world[id * 3 + 1]);
      component.maxY = Math.max(component.maxY, world[id * 3 + 1]);
      component.minZ = Math.min(component.minZ, world[id * 3 + 2]);
      component.maxZ = Math.max(component.maxZ, world[id * 3 + 2]);
      component.ids.push(id);
    }
    const shell = components.size > MAX_GARMENT_PIECES;
    const vertexRegion = new Array(count);
    const collarComponents = [];
    if (!shell) {
      const bodyCutoffY = bounds.minY + (bounds.maxY - bounds.minY) * 0.5;
      const minSleeveSpan = (bounds.maxX - bounds.minX) * 0.35;
      components.forEach((component) => {
        if (component.minY < bodyCutoffY) {
          component.region = (component.minZ + component.maxZ) / 2 > centerZ ? 'front' : 'back';
        } else if (component.maxX - component.minX >= minSleeveSpan) {
          component.region = (component.minX + component.maxX) / 2 < centerX ? 'left' : 'right';
        } else {
          component.region = 'collar';
        }
        if (component.region === 'collar') collarComponents.push(component);
        component.ids.forEach((id) => { vertexRegion[id] = component.region; });
      });
    }
    // One shell: a triangle facing the viewer (+Z) is on the front, any other on the back.
    // Sleeves hanging beside the body share those panels; the chest print area keeps designs
    // on the body.
    const facingRegion = (a, b, c) => {
      const ax = world[a * 3], ay = world[a * 3 + 1];
      const nz = (world[b * 3] - ax) * (world[c * 3 + 1] - ay) - (world[b * 3 + 1] - ay) * (world[c * 3] - ax);
      return nz > 0 ? 'front' : 'back';
    };
    // A shell with thickness also has inner faces: the inside of the back faces the viewer, so it
    // would get the front artwork and show through the neck opening. Only faces the front/back
    // camera actually sees are printed, and not drawstrings or flaps lying on the body.
    const hidden = shell ? this.hiddenFromCameras(world, total, vertexAt, bounds) : null;

    // Project each triangle corner into its region's camera view space (uniform units, y up).
    const cameras = this.buildProjectionCameras();
    const view = new Float32Array(total * 2);
    const triangleRegion = new Array(Math.floor(total / 3));
    const regionBounds = {};
    for (let offset = 0; offset + 2 < total; offset += 3) {
      const a = vertexAt(offset);
      let region = vertexRegion[a];
      if (shell) region = hidden[offset / 3] ? null : facingRegion(a, vertexAt(offset + 1), vertexAt(offset + 2));
      triangleRegion[offset / 3] = region;
      const camera = cameras[region];
      if (!camera) continue;
      const b = regionBounds[region] ||= { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
      for (let k = 0; k < 3; k += 1) {
        const id = vertexAt(offset + k);
        v.set(world[id * 3], world[id * 3 + 1], world[id * 3 + 2]).applyMatrix4(camera.matrixWorldInverse);
        view[(offset + k) * 2] = v.x;
        view[(offset + k) * 2 + 1] = v.y;
        b.minX = Math.min(b.minX, v.x);
        b.maxX = Math.max(b.maxX, v.x);
        b.minY = Math.min(b.minY, v.y);
        b.maxY = Math.max(b.maxY, v.y);
      }
    }

    // Fit each region into its panel, keeping proportions so artwork isn't stretched.
    const placement = {};
    PANEL_GROUPS.forEach((group) => {
      const present = group.filter((name) => regionBounds[name]);
      if (!present.length) return;
      const scale = Math.min(...present.map((name) => {
        const area = panelInnerRect(name);
        const b = regionBounds[name];
        return Math.min(area.width / (b.maxX - b.minX || 1), area.height / (b.maxY - b.minY || 1));
      }));
      present.forEach((name) => {
        const area = panelInnerRect(name);
        const b = regionBounds[name];
        placement[name] = {
          scale,
          bounds: b,
          left: area.x + (area.width - (b.maxX - b.minX) * scale) / 2,
          top: area.y + (area.height - (b.maxY - b.minY) * scale) / 2,
        };
      });
    });

    // The front piece on the board, for the chest print area (see chestArea)
    if (placement.front) {
      const b = placement.front.bounds;
      this.frontPieceRect = {
        x: placement.front.left,
        y: placement.front.top,
        width: (b.maxX - b.minX) * placement.front.scale,
        height: (b.maxY - b.minY) * placement.front.scale,
      };
      // A design uploaded before the model was ready moves onto the real chest
      if (this.chestPlacement) {
        this.layers.filter((layer) => layer.kind === 'image').forEach((layer) => this.fitChestLayer(layer));
        this.requestRender({ board: true });
      }
    }

    const collar = collarComponents.length ? this.unwrapCollar(collarComponents, world, count) : null;
    if (collar) {
      placement.collar = true;
      this.pieceRects = {
        ...this.pieceRects,
        collarFront: { x: 0, y: collar.rowTops[0], width: BOARD_W, height: collar.height },
        collarBack: { x: 0, y: collar.rowTops[1], width: BOARD_W, height: collar.height },
      };
    }

    // Board positions per triangle corner. The unrolled collar has seams where
    // each row ends (the two sides of the neck); a triangle crossing a seam is
    // laid out in one row and allowed to poke slightly past that row's end.
    // That needs corners that don't share vertices, hence the non-indexed
    // geometry below (the original indexed geometry is kept for GLB export).
    const corners = new Float32Array(total * 2).fill(-1);
    const cornerIds = new Uint32Array(total);
    for (let offset = 0; offset + 2 < total; offset += 3) {
      const ids = [vertexAt(offset), vertexAt(offset + 1), vertexAt(offset + 2)];
      const region = triangleRegion[offset / 3];
      const place = placement[region];
      ids.forEach((id, k) => {
        const o = offset + k;
        cornerIds[o] = id;
        if (!place || region === 'collar') return;
        corners[o * 2] = place.left + (view[o * 2] - place.bounds.minX) * place.scale;
        corners[o * 2 + 1] = place.top + (place.bounds.maxY - view[o * 2 + 1]) * place.scale;
      });
      if (!collar || region !== 'collar') continue;
      // Positions around the ring (0..1), unwrapped to be continuous within the triangle.
      const f0 = collar.along[ids[0]];
      const along = ids.map((id) => f0 + ((((collar.along[id] - f0) % 1) + 1.5) % 1) - 0.5);
      const mean = (along[0] + along[1] + along[2]) / 3;
      const turn = mean - Math.floor(mean);
      const row = turn < 0.5 ? 0 : 1;
      const rowStart = Math.floor(mean) + row * 0.5;
      ids.forEach((id, k) => {
        corners[(offset + k) * 2] = (along[k] - rowStart) * 2 * BOARD_W;
        corners[(offset + k) * 2 + 1] = collar.rowTops[row] + collar.depth[id];
      });
    }
    this.collarMarks = collar?.marks || [];
    const boardUv = new Float32Array(total * 2);
    for (let o = 0; o < total; o += 1) {
      const outside = corners[o * 2] < 0 && corners[o * 2 + 1] < 0;
      boardUv[o * 2] = outside ? -1 : corners[o * 2] / BOARD_W;
      boardUv[o * 2 + 1] = outside ? -1 : corners[o * 2 + 1] / BOARD_H;
    }
    this.sourceGeometry = geometry;
    const flat = index ? geometry.toNonIndexed() : geometry.clone();
    flat.setAttribute('vtBoard', new THREE.BufferAttribute(boardUv, 2));
    mesh.geometry = flat;

    // Triangles in board space: used to draw piece silhouettes in the guide and
    // to bake the board into the UV texture on GLB export. Kept as flat arrays:
    // a generated model can have millions of triangles.
    this.boardTriangles = {
      corners,
      cornerIds,
      uv,
      placed: Uint8Array.from(triangleRegion, (region) => (placement[region] ? 1 : 0)),
      regions: triangleRegion,
      collarRows: collar ? { rowTops: collar.rowTops, height: collar.height } : null,
    };
    this.silhouettePaths = null;
    this.silhouetteDirty = true;
    this.requestRender({ board: true });
  }

  // Per triangle: 1 when the front (+Z) or back (-Z) camera it faces gets no print on it:
  //  - another surface is in front of it from that camera (inner faces of a thick shell, the body
  //    under a drawstring);
  //  - it lies on top of the body, with a surface facing the same camera close behind it (a hoodie's
  //    drawstrings, hood flaps, pocket edges): printed on the flat garment, those cover the print.
  // Depth maps of the whole mesh along Z: the nearest z for the front camera, the farthest for the
  // back one, then the next surface facing that camera behind them.
  hiddenFromCameras(world, total, vertexAt, bounds) {
    const RES = 1024;
    const spanX = bounds.maxX - bounds.minX;
    const spanY = bounds.maxY - bounds.minY;
    const cell = Math.max(spanX, spanY) / (RES - 1) || 1;
    const width = Math.ceil(spanX / cell) + 1;
    const height = Math.ceil(spanY / cell) + 1;
    const pixelAt = (x, y) => Math.round((y - bounds.minY) / cell) * width + Math.round((x - bounds.minX) / cell);
    // Calls put(pixel, z, facing) for every pixel a triangle covers (+1 facing +Z, -1 facing -Z)
    const raster = (put) => {
      for (let offset = 0; offset + 2 < total; offset += 3) {
        const a = vertexAt(offset) * 3;
        const b = vertexAt(offset + 1) * 3;
        const c = vertexAt(offset + 2) * 3;
        const ax = (world[a] - bounds.minX) / cell, ay = (world[a + 1] - bounds.minY) / cell, az = world[a + 2];
        const bx = (world[b] - bounds.minX) / cell, by = (world[b + 1] - bounds.minY) / cell, bz = world[b + 2];
        const cx = (world[c] - bounds.minX) / cell, cy = (world[c + 1] - bounds.minY) / cell, cz = world[c + 2];
        const d = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
        const facing = d > 0 ? 1 : -1;
        // Triangles smaller than a pixel still count, at their centre
        put(Math.round((ay + by + cy) / 3) * width + Math.round((ax + bx + cx) / 3), (az + bz + cz) / 3, facing);
        if (Math.abs(d) < 1e-12) continue;
        const x0 = Math.max(0, Math.ceil(Math.min(ax, bx, cx)));
        const x1 = Math.min(width - 1, Math.floor(Math.max(ax, bx, cx)));
        const y0 = Math.max(0, Math.ceil(Math.min(ay, by, cy)));
        const y1 = Math.min(height - 1, Math.floor(Math.max(ay, by, cy)));
        for (let y = y0; y <= y1; y += 1) {
          for (let x = x0; x <= x1; x += 1) {
            const u = ((x - ax) * (cy - ay) - (y - ay) * (cx - ax)) / d;
            const v = ((bx - ax) * (y - ay) - (by - ay) * (x - ax)) / d;
            if (u < 0 || v < 0 || u + v > 1) continue;
            put(y * width + x, az + u * (bz - az) + v * (cz - az), facing);
          }
        }
      }
    };
    const size = width * height;
    const near = new Float32Array(size).fill(-Infinity);
    const far = new Float32Array(size).fill(Infinity);
    raster((pixel, z) => {
      if (z > near[pixel]) near[pixel] = z;
      if (z < far[pixel]) far[pixel] = z;
    });
    // Tolerance for slopes within a pixel; far below the distance between the front and the back
    const depth = bounds.maxZ - bounds.minZ;
    const eps = 0.02 * depth;
    // Something lying on the body: the body is at most this far behind it (a drawstring's tips stand
    // off the chest by up to about a tenth of the garment's depth; its back is half the depth away)
    const overlay = 0.15 * depth;
    // The next surface facing each camera behind the one it sees
    const nearBehind = new Float32Array(size).fill(-Infinity);
    const farBehind = new Float32Array(size).fill(Infinity);
    raster((pixel, z, facing) => {
      if (facing > 0 && z < near[pixel] - eps && z > nearBehind[pixel]) nearBehind[pixel] = z;
      if (facing < 0 && z > far[pixel] + eps && z < farBehind[pixel]) farBehind[pixel] = z;
    });
    const hidden = new Uint8Array(Math.floor(total / 3));
    for (let offset = 0; offset + 2 < total; offset += 3) {
      const a = vertexAt(offset) * 3;
      const b = vertexAt(offset + 1) * 3;
      const c = vertexAt(offset + 2) * 3;
      const nz = (world[b] - world[a]) * (world[c + 1] - world[a + 1]) - (world[b + 1] - world[a + 1]) * (world[c] - world[a]);
      const pixel = pixelAt((world[a] + world[b] + world[c]) / 3, (world[a + 1] + world[b + 1] + world[c + 1]) / 3);
      // Depths along the camera's view: larger = closer to it
      const sign = nz > 0 ? 1 : -1;
      const z = sign > 0 ? Math.max(world[a + 2], world[b + 2], world[c + 2]) : -Math.min(world[a + 2], world[b + 2], world[c + 2]);
      const top = sign > 0 ? near[pixel] : -far[pixel];
      const behind = sign > 0 ? nearBehind[pixel] : -farBehind[pixel];
      // Something lies on the body here: the body just behind it is printed (seen beside a
      // drawstring when the garment turns), the drawstring itself is not
      const lying = behind > top - overlay;
      if (z >= top - eps) hidden[offset / 3] = lying ? 1 : 0;
      else hidden[offset / 3] = lying && z >= behind - eps ? 0 : 1;
    }
    return hidden;
  }

  // Piece silhouettes for the guide, built the first time the guide draws them.
  buildSilhouettePaths() {
    const triangles = this.boardTriangles;
    if (!triangles) return [];
    const { corners, placed, regions, collarRows } = triangles;
    const paths = {};
    for (let t = 0; t < placed.length; t += 1) {
      const region = regions[t];
      if (!placed[t] || region === 'collar') continue;
      const o = t * 6;
      const ax = corners[o], ay = corners[o + 1];
      const bx = corners[o + 2], by = corners[o + 3];
      const cx = corners[o + 4], cy = corners[o + 5];
      const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
      if (Math.abs(cross) < 1e-9) continue;
      // Same winding for every triangle so overlapping front/back faces of a
      // sleeve don't cancel each other out under the non-zero fill rule.
      const path = paths[region] ||= new Path2D();
      path.moveTo(ax, ay);
      if (cross > 0) {
        path.lineTo(bx, by);
        path.lineTo(cx, cy);
      } else {
        path.lineTo(cx, cy);
        path.lineTo(bx, by);
      }
      path.closePath();
    }
    if (collarRows) {
      // The unrolled band is a clean strip; its triangle outline is only
      // vertex noise from the rib's folded edge, so show plain rows instead.
      paths.collar = new Path2D();
      collarRows.rowTops.forEach((top) => paths.collar.rect(0, top, BOARD_W, collarRows.height));
    }
    return Object.values(paths);
  }

  
  // The collar is a ring around the neck, so a camera projection would print
  // the same (mirrored) artwork on its front and back halves. Instead it is
  // unrolled like a flat rib pattern: arc length around the neck -> board x,
  // band height -> board y. The ring starts at the wearer's right side
  // (world -X); the front half fills the COLLAR FRONT row and the back half
  // the COLLAR BACK row, each reading left-to-right as seen from that side.
  // Returns per-vertex ring position (`along`, 0..1) and depth below the row
  // top; buildBoardProjection turns those into board positions per corner.
  unwrapCollar(components, world, count) {
    const BINS = 72;
    const start = -Math.PI / 2;
    const ring = components.reduce((a, b) => (b.ids.length > a.ids.length ? b : a));
    const all = components.flatMap((c) => c.ids);
    const xs = all.map((id) => world[id * 3]);
    const zs = all.map((id) => world[id * 3 + 2]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
    const binAt = (id) => {
      let angle = Math.atan2(world[id * 3] - cx, world[id * 3 + 2] - cz);
      if (angle < start) angle += Math.PI * 2;
      return ((angle - start) / (Math.PI * 2)) * BINS;
    };

    // Per angular bin: the band's lower/upper edge and radius, from the main ring.
    const lo = new Float64Array(BINS).fill(Infinity);
    const hi = new Float64Array(BINS).fill(-Infinity);
    const radius = new Float64Array(BINS);
    const samples = new Uint32Array(BINS);
    ring.ids.forEach((id) => {
      const b = Math.min(BINS - 1, Math.floor(binAt(id)));
      const y = world[id * 3 + 1];
      lo[b] = Math.min(lo[b], y);
      hi[b] = Math.max(hi[b], y);
      radius[b] += Math.hypot(world[id * 3] - cx, world[id * 3 + 2] - cz);
      samples[b] += 1;
    });
    for (let b = 0; b < BINS; b += 1) {
      if (samples[b]) {
        radius[b] /= samples[b];
        continue;
      }
      for (let d = 1; d < BINS; d += 1) {
        const n = [(b + d) % BINS, (b - d + BINS) % BINS].find((i) => samples[i]);
        if (n === undefined) continue;
        lo[b] = lo[n];
        hi[b] = hi[n];
        radius[b] = radius[n] / samples[n];
        break;
      }
    }

    // Smooth the edges (circular moving average) so lettering doesn't wobble
    // with the sparse vertex sampling of each 5° bin.
    const smooth = (values) => values.map((_, b) => {
      let sum = 0;
      for (let d = -2; d <= 2; d += 1) sum += values[(b + d + BINS) % BINS];
      return sum / 5;
    });
    lo.set(smooth(lo));
    hi.set(smooth(hi));

    // Arc length, so artwork keeps its proportions even though the neck opening isn't round.
    const arc = new Float64Array(BINS + 1);
    for (let b = 0; b < BINS; b += 1) arc[b + 1] = arc[b] + (radius[b] * Math.PI * 2) / BINS;
    const circumference = arc[BINS];
    let bandHeight = 0;
    for (let b = 0; b < BINS; b += 1) bandHeight += (hi[b] - lo[b]) / BINS;
    // Half the circumference spans the board width in each row.
    const area = panelInnerRect('collarFront');
    const height = Math.min(area.height, (bandHeight / (circumference / 2)) * BOARD_W);
    const rowTops = ['collarFront', 'collarBack'].map((name) => {
      const rect = panelInnerRect(name);
      return rect.y + (rect.height - height) / 2;
    });
    const fractionAt = (t) => {
      const b = Math.min(BINS - 1, Math.floor(t));
      return (arc[b] + (t - b) * (arc[b + 1] - arc[b])) / circumference;
    };

    const along = new Float32Array(count);
    const depth = new Float32Array(count);
    all.forEach((id) => {
      const t = binAt(id);
      const b = Math.min(BINS - 1, Math.floor(t));
      const span = hi[b] - lo[b] || 1;
      const h = Math.max(0, Math.min(1, (world[id * 3 + 1] - lo[b]) / span));
      along[id] = fractionAt(t);
      depth[id] = (1 - h) * height;
    });

    // Centre-front / centre-back guides (angle 0 and π around the neck).
    const tAtAngle = (angle) => ((angle - start) / (Math.PI * 2)) * BINS;
    const marks = [
      { x: fractionAt(tAtAngle(0)) * 2 * BOARD_W, top: rowTops[0], label: 'CENTER FRONT' },
      { x: (fractionAt(tAtAngle(Math.PI)) - 0.5) * 2 * BOARD_W, top: rowTops[1], label: 'CENTER BACK' },
    ].map((mark) => ({ ...mark, bottom: mark.top + height }));
    return { along, depth, height, rowTops, marks };
  }

  // Everything the configurator adds to the garment's standard material:
  //  - board artwork (vtBoard attribute -> board texture), with puff-print relief
  //  - acid wash from a precomputed noise field (shared with the GLB bake)
  //  - "knit" reveal and "waves" cloth motion, driven from loop()
  patchMaterial(material) {
    const uniforms = {
      uTint: { value: new THREE.Color(this.tintHex()) },
      uBoard: { value: this.boardTex },
      // At BOARD_SCALE texels, so the puff edge keeps its width on a sharper board
      uBoardTexel: { value: new THREE.Vector2(1 / (BOARD_W * BOARD_SCALE), 1 / (BOARD_H * BOARD_SCALE)) },
      uAcid: { value: this.acid },
      uAcidTex: { value: this.acidTexture },
      uPuff: { value: this.puff },
      uTime: { value: 0 },
      uWave: { value: 0 },
      uKnit: { value: KNIT_OFF },
      uHeight: { value: new THREE.Vector2(0, 1) },
    };
    material.userData.vt = uniforms;
    material.userData.baseSide = material.side;
    const prev = material.onBeforeCompile;
    material.onBeforeCompile = (shader) => {
      if (prev) prev(shader);
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           attribute vec2 vtBoard;
           varying vec2 vVtBoard;
           varying float vVtHeight;
           uniform float uTime;
           uniform float uWave;
           uniform vec2 uHeight;`
        )
        .replace(
          '#include <uv_vertex>',
          `#include <uv_vertex>
           vVtBoard = vtBoard;`
        )
        // The wave offset (and its effect on the normal) is computed right after
        // the normal is read, because lighting normals are derived before
        // begin_vertex; the offset itself is applied in begin_vertex.
        .replace(
          '#include <beginnormal_vertex>',
          `#include <beginnormal_vertex>
           float vtSpan = max(uHeight.y - uHeight.x, 1e-4);
           vVtHeight = clamp((position.y - uHeight.x) / vtSpan, 0.0, 1.0);
           vec3 vtOffset = vec3(0.0);
           if (uWave > 0.0) {
             // Cloth ripple: travelling waves along the normal, strongest at the hem.
             float vtK = 6.2831853 / vtSpan;
             float vtHang = 1.0 - vVtHeight;
             float vtAmp = uWave * vtSpan * 0.02 * (0.25 + vtHang);
             float vtP1 = position.y * vtK * 1.6 - uTime * 2.2 + position.x * vtK * 0.9;
             float vtP2 = position.x * vtK * 2.3 + uTime * 1.7;
             float vtRipple = sin(vtP1) + 0.5 * sin(vtP2);
             vtOffset = objectNormal * vtRipple * vtAmp;
             vtOffset.z += sin(position.x * vtK * 1.2 + uTime * 1.3) * uWave * vtSpan * 0.03 * vtHang * vtHang;
             // Tilt the normal by the ripple's slope so the folds catch the light.
             vec3 vtGrad = vtAmp * vec3(cos(vtP1) * vtK * 0.9 + 0.5 * cos(vtP2) * vtK * 2.3, cos(vtP1) * vtK * 1.6, 0.0);
             vtGrad -= dot(vtGrad, objectNormal) * objectNormal;
             objectNormal = normalize(objectNormal - vtGrad);
           }`
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           transformed += vtOffset;`
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform vec3 uTint;
           uniform sampler2D uBoard;
           uniform vec2 uBoardTexel;
           uniform float uAcid;
           uniform sampler2D uAcidTex;
           uniform float uPuff;
           uniform float uKnit;
           varying vec2 vVtBoard;
           varying float vVtHeight;

           float vtInk(vec2 uv) {
             return texture2D(uBoard, clamp(uv, 0.0, 1.0)).a * step(0.0, vVtBoard.x);
           }
           // Softened ink coverage = puff height, so the print swells with rounded edges.
           float vtPuffHeight(vec2 uv) {
             vec2 t = uBoardTexel * 2.5;
             float h = 0.0;
             for (int i = 0; i < 8; i++) {
               float a = float(i) * 0.7853982;
               h += vtInk(uv + vec2(cos(a), sin(a)) * t);
             }
             return (h + 2.0 * vtInk(uv)) / 10.0;
           }
           // Screen-space bump mapping (same approach as three.js' bumpmap chunk).
           vec3 vtPerturbNormal(vec3 surfPos, vec3 surfNorm, vec2 dHdxy, float faceDir) {
             vec3 sigmaX = normalize(dFdx(surfPos));
             vec3 sigmaY = normalize(dFdy(surfPos));
             vec3 r1 = cross(sigmaY, surfNorm);
             vec3 r2 = cross(surfNorm, sigmaX);
             float det = dot(sigmaX, r1) * faceDir;
             vec3 grad = sign(det) * (dHdxy.x * r1 + dHdxy.y * r2);
             return normalize(abs(det) * surfNorm - grad);
           }
           float vtHash(vec2 p) {
             p = fract(p * vec2(123.34, 456.21));
             p += dot(p, p + 45.32);
             return fract(p.x * p.y);
           }
           float vtNoise(vec2 p) {
             vec2 i = floor(p);
             vec2 f = fract(p);
             vec2 u = f * f * (3.0 - 2.0 * f);
             return mix(mix(vtHash(i), vtHash(i + vec2(1.0, 0.0)), u.x),
               mix(vtHash(i + vec2(0.0, 1.0)), vtHash(i + vec2(1.0, 1.0)), u.x), u.y);
           }`
        )
        .replace(
          '#include <map_fragment>',
          `#include <map_fragment>
           diffuseColor.rgb *= uTint;

           // Acid wash: bleached, slightly warm patches with darker rims where the
           // dye pooled. Same field and thresholds as acidWashPixel() in JS.
           if (uAcid > 0.0) {
             float vtN = texture2D(uAcidTex, vMapUv * ${ACID_SCALE.toFixed(3)}).r;
             float vtBleach = smoothstep(${ACID_LOW}, ${ACID_HIGH}, vtN);
             float vtVein = 1.0 - smoothstep(0.0, ${ACID_VEIN}, abs(vtN - ${ACID_LOW}));
             float vtLuma = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
             vec3 vtFaded = mix(vec3(vtLuma), vec3(1.0), 0.55) * vec3(1.0, 0.97, 0.93);
             diffuseColor.rgb = mix(diffuseColor.rgb, vtFaded, vtBleach * uAcid);
             diffuseColor.rgb *= 1.0 - vtVein * 0.25 * uAcid;
           }

           // Board layers, premultiplied alpha; vertices outside the board carry -1.
           vec4 vtDesign = texture2D(uBoard, clamp(vVtBoard, 0.0, 1.0)) * step(0.0, vVtBoard.x);
           diffuseColor.rgb = diffuseColor.rgb * (1.0 - vtDesign.a) + vtDesign.rgb;

           // Knit: the garment is revealed bottom-up; just below the ragged edge
           // the fabric shows knitted rows and a bright working thread.
           if (uKnit < ${KNIT_OFF.toFixed(1)}) {
             float vtEdge = uKnit + (vtNoise(vMapUv * vec2(90.0, 30.0)) - 0.5) * 0.035;
             if (vVtHeight > vtEdge) discard;
             float vtBand = 1.0 - smoothstep(0.0, 0.07, vtEdge - vVtHeight);
             float vtStitch = abs(fract(vMapUv.x * 220.0) - 0.5);
             float vtRib = smoothstep(0.15, 0.5, abs(fract(vVtHeight * 160.0 + vtStitch) - 0.5) * 2.0);
             diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * (0.55 + 0.6 * vtRib) + 0.06, vtBand);
             float vtThread = 1.0 - smoothstep(0.0, 0.008, vtEdge - vVtHeight);
             diffuseColor.rgb = mix(diffuseColor.rgb, uTint * 0.35 + 0.65, vtThread);
           }`
        )
        .replace(
          '#include <normal_fragment_maps>',
          `#include <normal_fragment_maps>
           // Puff print: raise the artwork using its (softened) alpha as a height map.
           if (uPuff > 0.0 && vVtBoard.x >= 0.0) {
             vec2 vtDx = dFdx(vVtBoard);
             vec2 vtDy = dFdy(vVtBoard);
             float vtH = vtPuffHeight(vVtBoard);
             vec2 vtDh = uPuff * ${PUFF_STRENGTH.toFixed(1)} * vec2(vtPuffHeight(vVtBoard + vtDx) - vtH, vtPuffHeight(vVtBoard + vtDy) - vtH);
             normal = vtPerturbNormal(-vViewPosition, normal, vtDh, faceDirection);
           }`
        );
      material.userData.shader = shader;
    };
    material.needsUpdate = true;
  }

  syncTint() {
    const uniforms = this.shirt?.material.userData.vt;
    if (!uniforms) return;
    uniforms.uTint.value.set(this.tintHex());
    uniforms.uAcid.value = this.acid;
    uniforms.uPuff.value = this.puff;
  }

  resize() {
    const stage = this.$('[data-vt-stage]');
    if (!stage || !this.renderer) return;
    const { clientWidth: w, clientHeight: h } = stage;
    this.camera.aspect = w / Math.max(h, 1);
    this.camera.updateProjectionMatrix();
    // Export quality only affects exported files; the viewer always renders sharp.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.fitBackground(this.camera.aspect);
    if (this.modelSize) {
      // Keep whatever zoom the user chose, relative to the new framing.
      const fit = this.fitDistance(this.camera.aspect);
      const ratio = fit / (this.fitDistanceValue || fit);
      this.setCameraDistance(this.cameraDistance() * ratio);
      if (this.zoomBaseDistance) this.zoomBaseDistance *= ratio;
      this.fitDistanceValue = fit;
      this.updateZoomLimits();
    }
  }

  // Camera distance at which the whole garment fits a viewport of this aspect.
  fitDistance(aspect) {
    const tan = Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2);
    const { x, y, z } = this.modelSize;
    return Math.max(y / 2 / tan, x / 2 / (tan * aspect)) * 1.15 + z / 2;
  }

  cameraDistance() {
    return this.camera.position.distanceTo(this.controls.target);
  }

  setCameraDistance(distance) {
    const offset = this.camera.position.clone().sub(this.controls.target);
    if (offset.lengthSq() === 0) offset.set(0, 0, 1);
    this.camera.position.copy(this.controls.target).add(offset.setLength(distance));
  }

  updateZoomLimits() {
    this.controls.minDistance = this.fitDistanceValue * 0.45;
    this.controls.maxDistance = this.fitDistanceValue * 2;
  }

  loop() {
    requestAnimationFrame(() => this.loop());
    const t = this.clock.getElapsedTime() * (0.4 + this.animSpeed * 2);
    if (this.model) {
      const base = this.model.userData.baseScale || 1;

      const basePosition =
        this.model.userData.basePosition || new THREE.Vector3();

      // Luôn quay về vị trí center chuẩn
      this.model.position.copy(basePosition);

      this.model.rotation.x = 0;
      this.model.rotation.y = 0;
      this.model.rotation.z = 0;

      // Scale mặc định
      this.model.scale.setScalar(base);

      if (this.anim === 'walk') {
        this.model.rotation.y = Math.sin(t) * 0.18;

        this.model.position.y =
          basePosition.y + Math.sin(t * 2) * 0.03;
      }
    }
    this.updateGarmentAnimation(t);
    if (this.camZoom && this.zoomBaseDistance) {
      // Only the distance changes, so orbiting and auto-rotate keep working.
      this.setCameraDistance(this.zoomBaseDistance * (1 + Math.sin(t * 0.6) * 0.15));
    }
    this.controls?.update();
    this.renderer?.render(this.scene, this.camera);
    if (this.recording) this.renderRecordingFrame();
  }

  // Waves and knit run in the garment shader; this feeds them time and state.
  updateGarmentAnimation(t) {
    const material = this.shirt?.material;
    const uniforms = material?.userData.vt;
    if (!uniforms) return;
    uniforms.uTime.value = t;
    // Ease the ripple in and out instead of snapping when switching animations.
    const wave = this.anim === 'waves' ? 1 : 0;
    uniforms.uWave.value += (wave - uniforms.uWave.value) * 0.06;
    if (!wave && uniforms.uWave.value < 0.001) uniforms.uWave.value = 0;

    const knitting = this.anim === 'knit';
    if (knitting) {
      // 80% of the cycle knits bottom -> top, the rest holds the finished tee.
      const phase = (t % KNIT_CYCLE) / KNIT_CYCLE;
      uniforms.uKnit.value = phase < 0.8 ? -0.05 + (phase / 0.8) * 1.1 : KNIT_OFF;
    } else {
      uniforms.uKnit.value = KNIT_OFF;
    }
    // While partly knitted the inside of the garment is visible.
    const side = knitting ? THREE.DoubleSide : material.userData.baseSide;
    if (material.side !== side) {
      material.side = side;
      material.needsUpdate = true;
    }
  }

  async exportFile(type) {
    if (this.recording) return;
    if (type === 'png') return this.exportPng();
    if (type === 'video') return this.exportVideo();
    if (type === 'glb') return this.exportGlb();
  }

  // Output size for the Size / Quality selects. Images double on "High";
  // video keeps the preset size (capped for encoders) and uses even numbers.
  exportDimensions(kind) {
    const size = this.$('[data-vt-export-size]')?.value || 'auto';
    const high = this.$('[data-vt-export-quality]')?.value === 'high';
    const stage = this.$('[data-vt-stage]');
    const presets = { portrait: [1080, 1920], square: [1600, 1600] };
    let [width, height] = presets[size] || [stage.clientWidth, stage.clientHeight];
    let limit = this.renderer.getContext().getParameter(this.renderer.getContext().MAX_RENDERBUFFER_SIZE);
    if (kind === 'image' && high) {
      width *= 2;
      height *= 2;
    }
    if (kind === 'video') limit = Math.min(limit, VIDEO_MAX_SIZE);
    const shrink = Math.min(1, limit / Math.max(width, height));
    width = Math.round(width * shrink);
    height = Math.round(height * shrink);
    if (kind === 'video') {
      width -= width % 2;
      height -= height % 2;
    }
    return [width, height];
  }

  exportPng() {
    const [width, height] = this.exportDimensions('image');

    // Render once at the export size with a matching aspect, re-framed so a
    // portrait or square crop still shows the same garment framing.
    const aspect = width / height;
    const prevPosition = this.camera.position.clone();
    if (this.modelSize) {
      this.setCameraDistance(this.cameraDistance() * (this.fitDistance(aspect) / this.fitDistance(this.camera.aspect)));
    }
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.fitBackground(aspect);
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, false);
    this.renderer.render(this.scene, this.camera);
    // toBlob snapshots the canvas synchronously, so the viewer is restored immediately.
    this.renderer.domElement.toBlob((blob) => downloadBlob(blob, 'mockup.png'), 'image/png');
    this.camera.position.copy(prevPosition);
    this.resize();
  }

  // ---------------------------------------------------------------------------
  // Video export: a second, off-screen renderer draws each frame at the export
  // size while MediaRecorder captures its canvas, so the viewer is untouched.
  // ---------------------------------------------------------------------------

  static pickVideoMime() {
    if (typeof MediaRecorder === 'undefined') return null;
    return [
      'video/mp4;codecs=avc1.42E01E',
      'video/mp4;codecs=avc1',
      'video/mp4',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
    ].find((type) => MediaRecorder.isTypeSupported(type)) || null;
  }

  async exportVideo() {
    const mime = VTConfigurator.pickVideoMime();
    if (!this.model || !mime || !HTMLCanvasElement.prototype.captureStream) {
      this.flashButton('[data-vt-export="video"]', 'Not supported in this browser');
      return;
    }
    const seconds = Number(this.$('[data-vt-video-duration]')?.value) || 8;
    const high = this.$('[data-vt-export-quality]')?.value === 'high';
    const [width, height] = this.exportDimensions('video');

    const canvas = document.createElement('canvas');
    // Kept in the document (but invisible) so every browser keeps producing frames.
    canvas.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none;';
    document.body.append(canvas);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(1);
    renderer.setSize(width, height, false);
    renderer.outputColorSpace = this.renderer.outputColorSpace;
    renderer.toneMapping = this.renderer.toneMapping;
    renderer.toneMappingExposure = this.renderer.toneMappingExposure;

    const aspect = width / height;
    const camera = this.camera.clone();
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
    const target = this.controls.target.clone();

    const stream = canvas.captureStream(high ? 60 : 30);
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: high ? 16e6 : 8e6 });
    const chunks = [];
    recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
    const stopped = new Promise((resolve) => { recorder.onstop = resolve; });

    this.recording = {
      renderer,
      camera,
      target,
      aspect,
      recorder,
      duration: seconds * 1000,
      start: 0,
      // Start from the viewer's angle and zoom, re-framed for the export aspect.
      offset: this.camera.position.clone().sub(target)
        .multiplyScalar(this.fitDistance(aspect) / this.fitDistance(this.camera.aspect)),
      baseDistance: this.cameraDistance(),
      // A still garment would make a still video: orbit it once instead.
      orbit: this.camSpin || this.anim === 'static',
    };
    this.setExportBusy(true);
    recorder.start(250);
    this.recording.start = performance.now();

    await stopped;
    const type = mime.split(';')[0];
    downloadBlob(new Blob(chunks, { type }), `mockup.${type === 'video/mp4' ? 'mp4' : 'webm'}`);
    stream.getTracks().forEach((track) => track.stop());
    renderer.dispose();
    renderer.forceContextLoss();
    canvas.remove();
    this.recording = null;
    this.setExportBusy(false);
  }

  renderRecordingFrame() {
    const rec = this.recording;
    if (!rec.start || rec.stopping) return;
    const progress = Math.min(1, (performance.now() - rec.start) / rec.duration);
    const offset = rec.offset.clone();
    if (rec.orbit) offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), progress * Math.PI * 2);
    // Follow the viewer's zoom animation, if it is running.
    offset.multiplyScalar(this.cameraDistance() / rec.baseDistance);
    rec.camera.position.copy(rec.target).add(offset);
    rec.camera.lookAt(rec.target);

    this.fitBackground(rec.aspect);
    rec.renderer.render(this.scene, rec.camera);
    this.fitBackground(this.camera.aspect);

    const label = this.$('[data-vt-export="video"]');
    if (label) label.textContent = `Recording ${Math.round(progress * 100)}%`;
    if (progress >= 1) {
      rec.stopping = true;
      rec.recorder.stop();
    }
  }

  setExportBusy(busy) {
    this.$all('[data-vt-export]').forEach((btn) => {
      btn.disabled = busy;
      btn.dataset.vtLabel ??= btn.textContent;
      if (!busy) btn.textContent = btn.dataset.vtLabel;
    });
    this.$('[data-vt-export="video"]')?.classList.toggle('is-recording', busy);
  }

  async exportGlb() {
    if (!this.shirt) return;
    const baked = this.bakeAlbedo();
    const bakedTex = new THREE.CanvasTexture(baked);
    bakedTex.colorSpace = THREE.SRGBColorSpace;
    bakedTex.flipY = false;
    const clone = this.model.clone(true);
    clone.traverse((obj) => {
      if (!obj.isMesh) return;
      // Export the original indexed geometry, not the per-corner copy used for display.
      const source = obj.geometry === this.shirt.geometry && this.sourceGeometry ? this.sourceGeometry : obj.geometry;
      obj.geometry = source.clone();
      obj.geometry.deleteAttribute('vtBoard');
      obj.material = obj.material.clone();
      obj.material.map = bakedTex;
      obj.material.color = new THREE.Color('#ffffff');
      obj.material.onBeforeCompile = undefined;
      obj.material.needsUpdate = true;
    });
    const exporter = new GLTFExporter();
    const result = await new Promise((resolve, reject) => {
      exporter.parse(clone, resolve, reject, { binary: true, embedImages: true });
    });
    const blob = new Blob([result], { type: 'model/gltf-binary' });
    downloadBlob(blob, 'custom-garment.glb');
  }

  bakeAlbedo() {
    const map = this.shirt?.material?.map;
    const src = map?.image;
    const canvas = document.createElement('canvas');
    canvas.width = src?.width || 2048;
    canvas.height = src?.height || 2048;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = this.tintHex();
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (src) {
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    }
    // Same order as the shader: acid wash on the fabric, then the print on top.
    if (this.acid > 0) this.bakeAcidWash(ctx, canvas.width, canvas.height);
    this.bakeBoardToUv(ctx, canvas.width, canvas.height);
    return canvas;
  }

  // Applies acidWashPixel() to every texel, in linear space like the shader.
  bakeAcidWash(ctx, width, height) {
    const toLinear = new Float32Array(256);
    for (let i = 0; i < 256; i += 1) {
      const c = i / 255;
      toLinear[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    }
    const SRGB_STEPS = 4096;
    const toSrgb = new Uint8ClampedArray(SRGB_STEPS + 1);
    for (let i = 0; i <= SRGB_STEPS; i += 1) {
      const c = i / SRGB_STEPS;
      toSrgb[i] = Math.round((c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055) * 255);
    }
    const image = ctx.getImageData(0, 0, width, height);
    const { data } = image;
    const out = [0, 0, 0];
    for (let y = 0; y < height; y += 1) {
      const v = ((y + 0.5) / height) * ACID_SCALE;
      for (let x = 0; x < width; x += 1) {
        const n = sampleAcidField(this.acidField, ((x + 0.5) / width) * ACID_SCALE, v);
        const i = (y * width + x) * 4;
        acidWashPixel(toLinear[data[i]], toLinear[data[i + 1]], toLinear[data[i + 2]], n, this.acid, out);
        data[i] = toSrgb[Math.round(Math.min(1, Math.max(0, out[0])) * SRGB_STEPS)];
        data[i + 1] = toSrgb[Math.round(Math.min(1, Math.max(0, out[1])) * SRGB_STEPS)];
        data[i + 2] = toSrgb[Math.round(Math.min(1, Math.max(0, out[2])) * SRGB_STEPS)];
      }
    }
    ctx.putImageData(image, 0, 0);
  }

  // CPU version of what the shader does: copy each board triangle into its UV
  // triangle. Only runs on export, and skips triangles no layer touches.
  bakeBoardToUv(ctx, width, height) {
    const bounds = this.contentBounds();
    if (!bounds || !this.boardTriangles) return;
    this.renderBoard();
    const { corners, cornerIds, uv, placed } = this.boardTriangles;
    for (let t = 0; t < placed.length; t += 1) {
      if (!placed[t]) continue;
      const o = t * 3;
      const xs = [corners[o * 2], corners[o * 2 + 2], corners[o * 2 + 4]];
      const ys = [corners[o * 2 + 1], corners[o * 2 + 3], corners[o * 2 + 5]];
      if (Math.max(...xs) < bounds.minX || Math.min(...xs) > bounds.maxX
        || Math.max(...ys) < bounds.minY || Math.min(...ys) > bounds.maxY) continue;
      const cross = (xs[1] - xs[0]) * (ys[2] - ys[0]) - (ys[1] - ys[0]) * (xs[2] - xs[0]);
      if (Math.abs(cross) < 1e-9) continue;
      mapTriangle(ctx, this.boardCanvas,
        [0, 1, 2].map((k) => ({ x: xs[k] * this.boardScale, y: ys[k] * this.boardScale })),
        [0, 1, 2].map((k) => ({ x: uv.getX(cornerIds[o + k]) * width, y: uv.getY(cornerIds[o + k]) * height })),
        0.75);
    }
  }

  contentBounds() {
    if (!this.layers.length) return null;
    const bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
    const include = (x, y, pad) => {
      bounds.minX = Math.min(bounds.minX, x - pad);
      bounds.maxX = Math.max(bounds.maxX, x + pad);
      bounds.minY = Math.min(bounds.minY, y - pad);
      bounds.maxY = Math.max(bounds.maxY, y + pad);
    };
    this.layers.forEach((layer) => {
      if (layer.kind === 'stroke') {
        layer.points.forEach((p) => include(p.x, p.y, layer.width));
        return;
      }
      const cos = Math.abs(Math.cos(layer.rotation || 0));
      const sin = Math.abs(Math.sin(layer.rotation || 0));
      include(layer.x, layer.y, 0);
      include(layer.x - (cos * layer.w + sin * layer.h) / 2, layer.y - (sin * layer.w + cos * layer.h) / 2, 0);
      include(layer.x + (cos * layer.w + sin * layer.h) / 2, layer.y + (sin * layer.w + cos * layer.h) / 2, 0);
    });
    return bounds;
  }
}

function start() {
  const root = document.querySelector('[data-vt-config]');
  if (root) new VTConfigurator(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
